/** English source catalog (source locale and fallback). Keys are stable English identifiers. */
export const en = {
  app: {
    name: 'Vammo Work',
    fixtureBanner: 'Fixture environment — sample data, no live integrations, no real messages.',
    skipToContent: 'Skip to content',
  },
  nav: {
    documents: 'Documents',
    myWork: 'My work',
    requests: 'Requests',
    work: 'Work',
    projects: 'Projects',
    teams: 'Teams',
    reports: 'Reports',
    admin: 'Administration',
    settings: 'Settings',
    newRequest: 'New request',
    newWork: 'New work item',
    search: 'Search',
    searchPlaceholder: 'Search work, requests and documents, or type a key…',
    notifications: 'Notifications',
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    openMenu: 'Open navigation',
    signOut: 'Sign out',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    autoTranslate: 'Automatic translation',
    language: 'Language',
    shortcuts: 'Keyboard shortcuts',
    tutorial: 'Tutorial',
  },
  repoPicker: {
    workTitle: 'Linked repositories',
    workHint: 'Linking repositories adds evidence for future work. Completed episodes keep their original basis.',
    projectTitle: 'Project repositories',
    projectHint: 'Reconciliation and capture coverage include repositories attached here.',
    search: 'Search repositories',
    noneSelected: 'No repositories selected.',
    remove: 'Remove {{name}}',
    archived: 'Archived',
    edit: 'Edit repositories',
  },
  translationReport: {
    action: 'Report',
    title: 'Report this translation',
    hint: 'Tell us what is wrong. The original text is never changed.',
    comment: 'What is wrong? (optional)',
    regenerate: 'Translate it again',
    submit: 'Send report',
    thanks: 'Thanks, the report was recorded',
  },
  languageCorrection: {
    label: 'Original language',
    hint: 'Correct it if the text was detected in the wrong language; translations are recreated.',
  },
  conflict: {
    title: 'Someone else saved first',
    hint: 'Compare your change with the current version, then choose which one to keep.',
    field: 'Field',
    mine: 'Your change',
    current: 'Current version',
    applyMine: 'Apply my change',
    keepCurrent: 'Keep current version',
    fields: { title: 'Title', descriptionMarkdown: 'Description', acceptanceCriteria: 'Acceptance criteria', priority: 'Priority', assigneeId: 'Assignee', labels: 'Labels', type: 'Type', originalLocale: 'Original language', repositoryIds: 'Repositories', problemMarkdown: 'Problem', expectedOutcome: 'Expected outcome' },
  },
  common: {
    removeItem: 'Remove {{name}}',
    viewPersonWork: 'See {{name}}\'s work',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    archive: 'Archive',
    confirm: 'Confirm',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    retry: 'Retry',
    reload: 'Reload',
    loading: 'Loading…',
    none: 'None',
    unassigned: 'Unassigned',
    unknown: 'Unknown',
    optional: 'optional',
    required: 'Required',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    me: 'Me',
    filter: 'Filter',
    clearFilters: 'Clear filters',
    sort: 'Sort',
    openFullPage: 'Open full page',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    viewOriginal: 'View original',
    viewTranslation: 'View translation',
    machineTranslated: 'Machine translated from {{language}}',
    translating: 'Translating…',
    translateItem: 'Translate',
    translateItemHint: 'Translate this item into your language',
    showOriginalItem: 'Show original',
    translationFailed: 'Translation unavailable; showing the original.',
    reportTranslation: 'Report translation',
    noResults: 'No results',
    comingSoon: 'Not available yet',
    reason: 'Reason',
    reasonPlaceholder: 'Explain the decision',
    created: 'Created',
    updated: 'Updated',
    by: 'by {{name}}',
    you: 'you',
    changesSaved: 'Changes saved',
    actionFailed: 'Action failed',
    count_one: '{{count}} item',
    count_other: '{{count}} items',
    preview: 'Preview',
    write: 'Write',
    markdownHint: 'Markdown supported: **bold**, _italic_, `code`, lists and links.',
    unsavedChanges: 'You have unsaved changes. Save or cancel them first.',
    discard: 'Discard',
    keepEditing: 'Keep editing',
  },
  errors: {
    title: 'Something went wrong',
    notFound: 'Not found, or you do not have access to it.',
    forbidden: 'You do not have permission to do that.',
    unauthenticated: 'Your session ended. Sign in again to continue.',
    conflict: 'Someone else changed this in the meantime. Review the latest version and reapply your edit.',
    staleGeneration: 'A newer delivery exists. Review the current state before responding.',
    rateLimited: 'Too many requests. Please wait a moment.',
    unavailable: 'This capability is not available right now.',
    offline: 'You appear to be offline. Changes will not be saved until the connection returns.',
    validation: 'Please correct the highlighted fields.',
    generic: 'Unexpected error. Reference: {{correlationId}}',
  },
  auth: {
    signIn: 'Sign in with Vammo',
    signingIn: 'Redirecting to sign in…',
    errorTitle: 'Sign-in did not complete',
    errorState: 'The sign-in link expired or was already used. Start again.',
    errorSuspended: 'Your account is suspended. Contact an administrator.',
    errorGeneric: 'We could not verify your corporate sign-in.',
    tryAgain: 'Try again',
  },
  status: {
    request: {
      DRAFT: 'Draft',
      SUBMITTED: 'Submitted',
      NEEDS_INFORMATION: 'Needs information',
      ACCEPTED: 'Accepted',
      REJECTED: 'Rejected',
      DUPLICATE: 'Duplicate',
      RESOLVED: 'Resolved',
      NEEDS_ATTENTION: 'Needs attention',
      CANCELED: 'Canceled',
    },
    phase: {
      TODO: 'To do',
      IN_PROGRESS: 'In progress',
      IN_REVIEW: 'In review',
      INTEGRATED: 'Integrated',
      DONE: 'Done',
      CANCELED: 'Canceled',
      EXPIRED: 'Expired',
    },
    priority: { URGENT: 'Urgent', HIGH: 'High', NORMAL: 'Normal', LOW: 'Low' },
    workType: {
      FEATURE: 'Feature',
      BUG: 'Bug',
      MAINTENANCE: 'Maintenance',
      INFRASTRUCTURE: 'Infrastructure',
      RESEARCH: 'Research',
      ANALYSIS: 'Analysis',
      OPERATIONS: 'Operations',
      REVIEW: 'Review',
      OTHER: 'Other',
      EPIC: 'Epic',
    },
    policy: { PRODUCTION: 'Production delivery', DELIVERABLE: 'Deliverable', REVIEWED_DELIVERABLE: 'Reviewed deliverable' },
    policyHelp: {
      PRODUCTION: 'Done only when every required change is verified in production.',
      DELIVERABLE: 'Done when the responsible person records the outcome against the criteria.',
      REVIEWED_DELIVERABLE: 'Done when an independent reviewer approves the outcome.',
    },
    delivery: { UNKNOWN: 'Delivery unknown', NOT_DEPLOYED: 'Not deployed', PARTIAL: 'Partially deployed', DEPLOYED: 'Deployed', REVERTED: 'Reverted' },
    association: { CONFIRMED: 'Confirmed', PROVISIONAL: 'Provisional' },
    visibility: { COMPANY: 'Company', RESTRICTED: 'Restricted' },
    visibilityHelp: { COMPANY: 'Visible to every signed-in Vammo employee.', RESTRICTED: 'Visible only to the selected people, teams and groups (and Superadmins).' },
    basis: { AGENT_REPORTED: 'Agent reported', HUMAN_REPORTED: 'Reported', PROVIDER_VERIFIED: 'Verified', IMPORTED_SOURCE: 'Imported', FIXTURE: 'Fixture' },
    completionBasis: { VERIFIED_PRODUCTION: 'Verified in production', DELIVERABLE: 'Deliverable recorded', REVIEWED_DELIVERABLE: 'Reviewed and approved', IMPORTED_SOURCE: 'Done in Jira (imported)', UNVERIFIED: 'Closed without evidence' },
    confirmation: { UNCONFIRMED: 'Awaiting your confirmation', CONFIRMED: 'Confirmed resolved', PERSISTS: 'Still happening' },
    fulfillment: {
      NOT_STARTED: 'Not started',
      IN_PROGRESS: 'Being worked on',
      IN_REVIEW: 'In review',
      AWAITING_DEPLOYMENT: 'Awaiting production deployment',
      AWAITING_ACTIVATION: 'Awaiting activation',
      DELIVERED: 'Delivered',
      NONE_LINKED: 'Not yet planned',
    },
    channel: { WEB: 'Web', SLACK: 'Slack', MCP: 'AI assistant', API: 'API', EMAIL: 'Email', JIRA_IMPORT: 'Jira import', SYSTEM: 'System', VAMMO_APPS: 'Vammo Apps' },
    scope: { NONE: 'Not applicable', AWAITING_SCOPE: 'Awaiting delivery scope', UNCONFIRMED: 'Scope not confirmed', CONFIRMED: 'Scope confirmed' },
    scopeAutomatic: 'Derived from the linked code and the service mapping',
    scopeNotAffected: 'Not affected by this change',
    milestone: { START: 'Started', SCOPE_CHANGE: 'Scope changed', BLOCKER: 'Blocker', VERIFICATION: 'Verification', PR_UPDATE: 'Pull request', HANDOFF: 'Handoff', SESSION_END: 'Session ended', NOTE: 'Note' },
    client: { CLAUDE_CODE: 'Claude Code', CODEX: 'Codex', CLAUDE: 'Claude', CHATGPT: 'ChatGPT', CLAWBOT: 'Clawbot (Slack)', OTHER: 'Other client' },
  },
  actions: {
    request: {
      submit: 'Submit request',
      cancel: 'Cancel request',
      ask_information: 'Ask for information',
      answer_information: 'Send answer',
      accept: 'Accept',
      reject: 'Reject',
      mark_duplicate: 'Mark as duplicate',
      fulfill: 'Mark fulfilled',
      report_persistence: 'Report still happening',
      fulfillment_invalidated: 'Reopen for attention',
      accept_correction: 'Accept correction',
      fulfill_after_review: 'Resolve after review',
      reconsider: 'Reconsider',
    },
    work: {
      start: 'Start',
      request_review: 'Request review',
      request_changes: 'Request changes',
      integration_observed: 'Integrated',
      production_verified: 'Verified in production',
      complete_deliverable: 'Complete',
      approve_deliverable: 'Approve',
      cancel: 'Cancel work',
      reopen: 'Reopen',
      coverage_lost: 'Coverage lost',
      close_without_evidence: 'Close without evidence',
      expire: 'Expire',
    },
  },
  myWork: {
    title: 'My work',
    greeting: 'Hello, {{name}}',
    subtitle: 'What needs your attention today.',
    assigned: 'Assigned to me',
    assignedEmpty: 'Nothing assigned to you right now.',
    myRequests: 'My requests',
    myRequestsEmpty: 'You have not filed any requests yet.',
    needsMe: 'Waiting on me',
    needsMeEmpty: 'Nothing is waiting on you.',
    blocked: 'Blocked',
    triageInbox: 'Triage inbox',
    updates: 'Recent updates',
    updatesEmpty: 'No updates yet.',
    following: 'Following',
    followingEmpty: 'Follow work or requests to see them here.',
    overdue: 'Overdue',
    overdueEmpty: 'Nothing of yours is overdue.',
    dueSoon: 'Due in the next 7 days',
    dueSoonEmpty: 'Nothing of yours is due this week.',
    stats: { open: 'Open work', inReview: 'In review', blocked: 'Blocked', attention: 'Requests needing attention', overdue: 'Overdue' },
  },
  requests: {
    move: { noProject: 'No project', title: 'Move to {{name}}', hint: 'Moving changes who can read this request and its comments and files.', restrictedAfter: 'After the move the request will be restricted to the project audience.', companyAfter: 'After the move the request will be visible company-wide.', losesAccess: 'These people would lose access:', nobodyLoses: 'Nobody involved loses access.', reassignFirst: 'The assignee would lose access. Reassign the request first.', confirm: 'Move request' },
    activation: { pending: 'Activation pending:', observed: 'Activation observed by {{name}} on {{time}}', notRequired: 'No separate activation required.', require: 'Require activation', record: 'Record activation', waive: 'Waive activation', requireHint: 'Use this when shipping is not enough, for example when a feature flag must be turned on. The request stays accepted until activation is recorded.', recordHint: 'Record who observed the change live and the evidence. This is audited.', waiveHint: 'Activation will no longer be required. The reason is audited.', noteLabel: 'What must be activated?', evidenceLabel: 'Evidence', reasonLabel: 'Reason' },
    duplicates: { title: 'Possible duplicates', hint: 'Similar requests you can see. Marking keeps each requester informed.', mark: 'Mark as duplicate of this' },
    title: 'Requests',
    subtitle: 'Problems, ideas and asks from across Vammo.',
    tabs: { inbox: 'Inbox', mine: 'Mine', attention: 'Needs attention', following: 'Following', all: 'All', snoozed: 'Snoozed' },
    empty: 'No requests here.',
    emptyInbox: 'The inbox is clear. New requests appear here for triage.',
    columns: { key: 'Key', title: 'Title', status: 'Status', priority: 'Priority', author: 'Requester', assignee: 'Assignee', team: 'Team', updated: 'Updated', created: 'Created', source: 'Source' },
    snooze: {
      action: 'Snooze',
      title: 'Snooze request',
      hint: 'It leaves the triage inbox and returns on its own at the chosen time. Any status change brings it back earlier.',
      until: 'Snooze until',
      tomorrow: 'Tomorrow morning',
      nextWeek: 'Next Monday',
      inTwoWeeks: 'In two weeks',
      custom: 'Pick a date',
      badge: 'Snoozed until {{time}}',
      wake: 'Wake up now',
      snoozedToast: 'Snoozed until {{time}}',
      wokenToast: 'Back in the inbox',
    },
    detail: {
      problem: 'Problem',
      currentBehavior: 'What happens now',
      expectedOutcome: 'Expected outcome',
      impact: 'Impact',
      impactUnknown: 'Impact not known yet',
      reproduction: 'How to reproduce',
      affectedArea: 'Affected area',
      progress: 'Progress',
      decisions: 'Decision history',
      comments: 'Comments',
      linkedWork: 'Linked work',
      linkedWorkEmpty: 'No work linked yet.',
      required: 'Required',
      optional: 'Optional',
      hiddenWork: 'Some linked work is restricted and not shown.',
      pendingQuestion: 'The team asked',
      answerPlaceholder: 'Answer the question…',
      duplicateOf: 'Duplicate of',
      decisionReason: 'Decision note',
      source: 'Source',
      triage: 'Triage',
      triageTeam: 'Triage team',
      followers: 'Follow',
      following: 'Following',
      linkWork: 'Link work',
      linkWorkSearch: 'Search work by key or title',
      unlink: 'Unlink',
    },
    confirm: {
      fromEmail: 'Choose your answer below. Nothing is recorded until you click.',
      title: 'Was this resolved for you?',
      body: 'The team marked this request as resolved. Let them know if it works for you.',
      confirmed: 'Yes, it is resolved',
      persists: 'No, it is still happening',
      commentPlaceholder: 'Add details (optional)',
      thanks: 'Thanks for confirming.',
      reported: 'Thanks — the team will review it again.',
      generation: 'Delivery #{{generation}}',
    },
    dialogs: {
      rejectTitle: 'Reject request',
      duplicateTitle: 'Mark as duplicate',
      duplicateSearch: 'Find the canonical request',
      askTitle: 'Ask for information',
      askPlaceholder: 'What do you need to know?',
      fulfillTitle: 'Mark as fulfilled',
      fulfillDirect: 'Describe the delivered outcome',
      reviewTitle: 'Resolve after review',
      reviewPlaceholder: 'Explain why the current result now fulfills the request',
      correctionTitle: 'Accept correction',
      correctionWork: 'Corrective work item (optional)',
      cancelTitle: 'Cancel request',
      reconsiderTitle: 'Reconsider request',
    },
  },
  comments: {
    react: 'Add reaction',
    summary: { action: 'Summarize the discussion', title: 'AI summary', again: 'Regenerate', note: 'Made by AI from the {{count}} comments you can see. It can be wrong; the thread is the source.' },
    reactedBy: '{{names}} reacted with {{reaction}}',
    reactions: { THUMBS_UP: 'thumbs up', THUMBS_DOWN: 'thumbs down', LAUGH: 'laugh', HOORAY: 'hooray', CONFUSED: 'confused', HEART: 'heart', ROCKET: 'rocket', EYES: 'eyes' },
    mentionPeople: 'Mention someone',
    mentionHint: 'Type @ to mention someone; they get a notification.',
  },
  labels: {
    title: 'Labels',
    manage: 'Manage labels…',
    hint: 'Give labels a color and a description. Labels without one show in gray; deleting one only removes its color, never the label from work items.',
    name: 'Label',
    color: 'Color',
    description: 'Description (optional)',
    add: 'Save label',
    saved: 'Label saved',
    delete: 'Delete the color of {{name}}',
    empty: 'No label has a color yet.',
    colors: { gray: 'Gray', red: 'Red', orange: 'Orange', amber: 'Yellow', green: 'Green', teal: 'Teal', blue: 'Blue', indigo: 'Indigo', purple: 'Purple', pink: 'Pink' },
  },
  inbox: {
    title: 'Inbox',
    subtitle: 'Updates on work and requests you take part in.',
    tabs: { inbox: 'All', mentions: 'Mentions', unread: 'Unread', snoozed: 'Snoozed', archived: 'Archived' },
    empty: { inbox: 'You are all caught up.', mentions: 'Nobody mentioned you yet.', unread: 'Nothing unread.', snoozed: 'Nothing snoozed.', archived: 'Nothing archived.' },
    markAllRead: 'Mark all as read',
    allRead: 'All marked as read',
    archiveRead: 'Archive read',
    archivedRead: 'Read notifications archived',
    markRead: 'Mark as read',
    archive: 'Archive',
    unarchive: 'Move back to the inbox',
    snooze: 'Snooze',
    snoozeFor: { day: 'Until tomorrow', threeDays: 'For 3 days', week: 'For a week' },
    snoozed: 'Snoozed',
    unsnooze: 'Bring back now',
    snoozedUntil: 'snoozed until {{time}}',
    unread: 'Unread',
    unreadCount_one: '{{count}} unread',
    unreadCount_other: '{{count}} unread',
    actions: 'Notification actions',
  },
  templates: {
    use: 'Start from a template…',
    manage: 'Manage',
    manageWork: 'Work item templates',
    manageRequest: 'Request templates',
    saveAs: 'Save as template',
    saveTitle: 'Save as template',
    saveHintWork: 'Saves the title, type, priority, description, criteria, labels and sub-items so the next item starts from them.',
    saveHintRequest: 'Saves the request text so the next request starts from it.',
    name: 'Template name',
    description: 'Description (optional)',
    onlyThisProject: 'Offer it only in this project',
    saved: 'Template saved',
    everywhere: 'All projects',
    remove: 'Remove template {{name}}',
    empty: 'No templates yet. Save one from a form or a work item.',
    subItems_one: 'Creates {{count}} sub-item',
    subItems_other: 'Creates {{count}} sub-items',
  },
  intake: {
    title: 'New request',
    subtitle: 'Describe what is happening. You do not need to know how to fix it.',
    tabs: { assistant: 'Guided', form: 'Form' },
    assistantIntro: 'Tell me about the problem or idea in your own words. I will ask only for what is missing.',
    assistantPlaceholder: 'Type your answer…',
    assistantUnavailable: 'The assistant is unavailable right now. Use the form; everything is saved.',
    assistant: {
      ack: 'Thanks, I saved that in the draft.',
      askProblem: 'What is happening, and where?',
      askExpected: 'What should happen instead?',
      askImpact: 'Who is affected, and how much does it matter? It is fine to say you do not know.',
      askReproduction: 'If it is a bug: how can someone see it happen, and how often?',
      ready: 'I have what triage needs. Review the summary and submit when it looks right.',
      fallback: 'The assistant is unavailable, so I saved your answer as-is.',
    },
    fields: {
      title: 'Short title',
      titlePlaceholder: 'e.g. Rider app does not show swap history',
      problem: 'What is the problem?',
      problemPlaceholder: 'Describe the situation and who is affected',
      currentBehavior: 'What happens now?',
      expectedOutcome: 'What should happen instead?',
      impact: 'Impact',
      impactPlaceholder: 'Who or what is affected, and how much?',
      impactUnknown: 'I do not know the impact yet',
      reproduction: 'Steps to reproduce (for bugs)',
      affectedArea: 'Affected area or product',
      language: 'Written in',
      visibility: 'Who can see this',
      team: 'Route to team',
      teamAuto: 'Let triage decide',
      priority: 'Priority',
      evidence: 'Attachments',
    },
    duplicates: 'Possibly related requests',
    duplicatesHelp: 'Check whether one of these already covers your request.',
    review: 'Review and submit',
    reviewHelp: 'Check the summary below. You can still edit before submitting.',
    missing: 'Still missing: {{fields}}',
    saved: 'Draft saved',
    submitted: 'Request {{key}} submitted',
    draftNote: 'Drafts are private until you submit them.',
  },
  work: {
    title: 'Work',
    defectCause: {
      title: 'Cause',
      causedBy: 'Caused by',
      causedByHelp: 'The delivered work that introduced this bug (up to 5).',
      none: 'No delivery cause',
      noneHelp: 'No delivered work caused it.',
      category: 'Category',
      note: 'Note',
      notePlaceholder: 'What happened?',
      reasons: { PRE_EXISTING: 'Pre-existing', EXTERNAL: 'External or vendor', CONFIGURATION: 'Configuration', DATA: 'Data', OTHER: 'Other' },
      search: 'Search the work that caused it',
      notInformed: 'No cause recorded',
      notInformedHint: 'Say which work caused this bug, or that no delivery caused it.',
      inform: 'Add cause',
      change: 'Change',
      changeTitle: 'Change cause',
      clear: 'Clear',
      saved: 'Cause saved',
      noDeliveryCause: 'No delivery cause: {{reason}}',
    },
    subtitle: 'Everything in flight across teams, projects and repositories.',
    views: { list: 'List', board: 'Board' },
    empty: 'No work matches these filters.',
    columns: { key: 'Key', title: 'Title', phase: 'Phase', priority: 'Priority', type: 'Type', assignee: 'Assignee', project: 'Project', team: 'Team', updated: 'Updated', delivery: 'Delivery', labels: 'Labels', created: 'Created', owner: 'Owner', start: 'Start', due: 'Due', points: 'Points' },
    filters: { phase: 'Phase', type: 'Type', priority: 'Priority', assignee: 'Assignee', project: 'Project', team: 'Team', blocked: 'Blocked only', provisional: 'Provisional only', includeCanceled: 'Include canceled', label: 'Label', archived: 'Archived only', due: 'Due date', dueOverdue: 'Overdue', dueSoon: 'Due in the next 7 days', dueNone: 'No due date' },
    bulk: { selected: '{{count}} selected', setPriority: 'Set priority', assign: 'Assign', addLabel: 'Add label', clear: 'Clear selection', setProject: 'Move to project', noProject: 'No project', setTeam: 'Set team', removeLabel: 'Remove label', labelPlaceholder: 'Label', archive: 'Archive', restore: 'Restore', setDue: 'Set due date', clearDue: 'Clear due date', cancel: 'Cancel work', cancelTitle: 'Cancel {{count}} work items', cancelHint: 'Every selected item is canceled with this reason.' },
    columnsMenu: { title: 'Columns', reset: 'Show default columns' },
    export: { title: 'Export', csv: 'CSV file', xlsx: 'Excel file (.xlsx)', hint: 'Downloads the current filters, up to 5000 rows.' },
    groupBy: 'Group',
    groupNone: 'No grouping',
    lanes: 'Rows',
    lanesNone: 'No rows',
    closedWindow: { label: 'Closed', days: 'Closed: last {{count}} days', all: 'Closed: all' },
    groupFields: { phase: 'Phase', priority: 'Priority', assignee: 'Assignee', project: 'Project', team: 'Team', type: 'Type', label: 'Label' },
    groups: { noProject: 'No project', noTeam: 'No team', noLabel: 'No label' },
    board: { moveTo: 'Move to…', dropRejected: 'That move is not allowed: {{reason}}', productionHint: 'Production work reaches Done only from verified deployments.' },
    create: {
      title: 'New work item',
      responsibility: 'Responsible',
      team: 'Team',
      personal: 'Personal (me)',
      project: 'Project',
      noProject: 'No project',
      type: 'Type',
      policy: 'Completion',
      description: 'Description',
      criteria: 'Acceptance criteria',
      addCriterion: 'Add criterion',
      criterionPlaceholder: 'Observable result…',
      assignee: 'Assignee',
      labels: 'Labels',
      labelsPlaceholder: 'comma, separated',
      startDate: 'Start date',
      dueDate: 'Due date',
      created: '{{key}} created',
    },
    provisional: {
      confirm: 'Confirm as new work',
      merge: 'Merge into existing work',
      mergeTitle: 'Merge into existing work',
      mergeHint: 'The key becomes an alias of the chosen item. Contributions, sessions, pull requests and request links move with it; nothing is deleted.',
      mergeInto: 'Merge into {{key}}',
      confirmed: 'Confirmed as new work',
      merged: 'Merged into {{key}}',
    },
    detail: {
      overview: 'Overview',
      activity: 'Activity',
      progress: 'Progress',
      delivery: 'Delivery',
      code: 'Code',
      codeTab: {
        pullRequests: 'Pull requests',
        commits: 'Commits',
        evidence: 'Links reported by agents',
        noPullRequests: 'No pull requests linked.',
        noCommits: 'No commits linked.',
        linkLabel: 'Link a commit or pull request',
        linkHint: 'Paste its GitHub URL. Commits and pull requests that mention {{key}} link themselves.',
        link: 'Link',
        linkedCommit: 'Commit linked',
        linkedPr: 'Pull request linked',
        unlink: 'Unlink',
        unlinked: 'Link removed',
        viaKey: 'By key',
        viaVammoApps: 'From Vammo Apps',
        manual: 'Linked by hand',
        prState: {
          OPEN: 'Open',
          CLOSED: 'Closed',
          MERGED: 'Merged',
        },
        errors: {
          not_connected: 'That repository is not connected. Install the GitHub App on it first.',
          not_synced: 'That pull request has not been synchronized yet. Attach the repository to a project and try again in a few minutes.',
          not_found: 'No commit with that SHA exists in the repository.',
          unsupported: 'Paste a GitHub commit or pull request URL.',
          invalid_format: 'Paste a GitHub commit or pull request URL.',
        },
      },
      history: 'History',
      description: 'Description',
      noDescription: 'No description yet.',
      criteria: 'Acceptance criteria',
      noCriteria: 'No acceptance criteria.',
      properties: 'Properties',
      owner: 'Owner',
      reviewer: 'Reviewer',
      repositories: 'Repositories',
      noRepositories: 'No repositories — that is fine for non-code work.',
      labels: 'Labels',
      blockers: 'Blockers',
      addBlocker: 'Add blocker',
      blockerReason: 'What is blocking?',
      preventsCompletion: 'Prevents completion',
      resolveBlocker: 'Resolve',
      relations: 'Related work',
      addRelation: 'Link work',
      relationKinds: { PARENT_OF: 'Parent of', DEPENDS_ON: 'Depends on', RELATES_TO: 'Relates to', DUPLICATES: 'Duplicates', CAUSED_BY: 'Caused by' },
      defect: 'Defect',
      requiresCompletion: 'Required for completion',
      contributors: 'Contributors',
      linkedRequests: 'Requests',
      outcome: 'Outcome',
      outcomePlaceholder: 'What was delivered?',
      complete: 'Complete deliverable',
      criterionStatus: { OPEN: 'Open', MET: 'Met', NOT_MET: 'Not met', WAIVED: 'Waived' },
      episodes: 'Completion episodes',
      provisionalNote: 'Created provisionally by an agent; confirm or merge it.',
      blockedNote: 'Blocked',
      awaitingDeployment: 'Awaiting deployment verification',
      sessions: 'Agent sessions',
      evidence: 'Evidence',
      noEvidence: 'No evidence attached.',
      archivedBanner: 'Archived {{time}}. Restore it to make changes.',
      archive: 'Archive',
      unarchive: 'Restore',
      archivedToast: 'Work item archived',
      restoredToast: 'Work item restored',
      delete: 'Delete permanently',
      deleteTitle: 'Delete {{key}} permanently?',
      deleteHint: 'This removes the work item with its comments, attachments, translations and progress. The audit log keeps a record with your reason. It cannot be undone.',
      deleteConfirm: 'Type {{key}} to confirm',
      deletedToast: '{{key}} deleted',
      addSubItem: 'Add sub-item',
      subItemOf: 'Sub-item of {{key}}',
      copyBranch: 'Copy branch name',
      branchCopied: 'Branch name copied: {{branch}}',
      copyKey: 'Copy key',
      keyCopied: '{{key}} copied',
      editLabels: 'Edit labels',
      labelsPlaceholder: 'Type a label and press Enter',
      noLabels: 'No labels',
      shortcuts: { title: 'Keyboard shortcuts', priority: 'Change priority', assign: 'Change assignee', assignMe: 'Assign to me', labels: 'Edit labels', copyKey: 'Copy key' },
      startDate: 'Start date',
      dueDate: 'Due date',
      overdue: 'Overdue',
      dueToday: 'Due today',
      clearDate: 'Clear date',
      subItems: 'Sub-items',
      noSubItems: 'No sub-items yet.',
      unlinkSubItem: 'Remove from sub-items',
      parents: 'Parent items',
      progressOf: '{{done}} of {{total}} done',
      timelineEmpty: 'No history yet.',
    },
  },
  projects: {
    slack: {
      title: 'Slack updates',
      hint: 'Choose the Slack channel where this project’s updates are posted, and which ones. Project managers and administrators can change it.',
      notConnected: 'Slack is not connected in this environment.',
      listFailed: 'Slack did not list its channels right now; try again in a minute.',
      channel: 'Channel',
      none: 'No channel',
      private: 'private',
      notMember: 'The app is not in #{{channel}} yet. Invite it in Slack with /invite @Vammo Work Tracker, or updates will not be posted.',
      replaces_one: 'An administrator set up {{count}} channel; saving keeps only the one chosen here.',
      replaces_other: 'An administrator set up {{count}} channels; saving keeps only the one chosen here.',
      workEvents: 'Tasks',
      requestEvents: 'Requests',
      save: 'Save Slack channel',
      saved: 'Slack channel saved',
      removed: 'The project no longer posts to Slack',
    },
    vammoApps: {
      tabs: { STANDARD: 'Projects', VAMMO_APP: 'Vammo Apps' },
      badge: 'Vammo App',
      open: 'Open in Vammo Apps',
      hint: 'One project per app of the Vammo Apps platform. Each deploy request is a work item that completes on its own once it runs in production.',
      empty: 'No Vammo Apps yet.',
      managed: 'Kept in sync by the Vammo Apps platform: work items follow this app’s deploy requests and deployments.',
    },
    activity: {
      subtitle: 'Work, delivery and participation across your projects.',
      search: 'Search projects by name or key…',
      includeArchived: 'Include archived',
      noResults: 'No projects match your search.',
      loaded: '{{count}} projects loaded',
      weekHint: 'This week · Monday to now · {{timezone}}',
      scopeHint:
        'Counts respect your access. Commits and successful production deployments cover linked repositories and may overlap between projects. Only observed activity is shown; missing data is not zero.',
      openProject: 'Open {{name}}',
      archived: 'Archived',
      repositoryCount_one: "{{count}} repository",
      repositoryCount_other: "{{count}} repositories",
      lastActivity: 'Last activity',
      noActivity: 'No recorded activity yet',
      open: 'Open tasks',
      inProgress: '{{count}} in progress',
      blocked_one: "{{count}} blocked",
      blocked_other: "{{count}} blocked",
      openHint:
        'Current unarchived tasks you can access, excluding completed, canceled and expired work. Blocked tasks are counted once.',
      completed: 'Completed',
      thisWeek: 'This week',
      imported_one: "+ {{count}} imported",
      imported_other: "+ {{count}} imported",
      completedHint:
        'Distinct tasks with a completion episode this week, including tasks reopened afterward. Repeated completions count once. Canceled, expired, archived and provisional work is excluded. Imported completions are shown separately; completion does not necessarily mean verified production delivery.',
      commits: 'Commits',
      deployments: 'Deploys',
      commitsHint:
        'Distinct observed commits in linked project repositories, using the source commit date. Shared repositories may appear in more than one project. Ingestion does not guarantee complete history.',
      deploymentsHint:
        'Distinct successful production deployments of services mapped to project repositories, using completion time. Failed attempts, rollbacks and deployments without a completion time are excluded. This is service activity, not proof that every project task was delivered.',
      activePeople: 'Active people',
      peopleHint:
        'Distinct active accounts that changed visible tasks, commented on them or updated the project this week. Includes agents acting for a verified person. Historical imports, system events and merely assigned people do not count. This is activity, not online presence or a productivity score.',
      lastSync: 'Last successful sync: {{date}}.',
      coverage: {
        PARTIAL: 'Observed · partial',
        FIXTURE: 'Fixture data',
        UNAVAILABLE: 'No synced data',
        UNMAPPED: 'No mapping',
        FORBIDDEN: 'Restricted',
      },
    },
    automation: {
      title: 'Automatic rules',
      hint: 'Applied every hour to this project’s work. Leave a field empty to turn its rule off.',
      staleAfter: 'No activity for (days)',
      staleAction: 'Then',
      actions: { NOTIFY: 'Remind the assignee', CANCEL: 'Cancel it (requested work only gets a reminder)' },
      autoArchive: 'Archive finished work after (days)',
      autoArchiveHint: 'Completed or canceled work leaves the lists; it stays searchable under Archived.',
      off: 'Off',
    },
    updates: {
      title: 'Status updates',
      statusLabel: 'Project status',
      status: { ON_TRACK: 'On track', AT_RISK: 'At risk', OFF_TRACK: 'Off track' },
      placeholder: 'What changed, what is at risk, what is next?',
      post: 'Post update',
      posted: 'Status update posted',
      empty: 'No status updates yet.',
      edited: 'edited',
      reply: 'Reply',
      replyPlaceholder: 'Write a reply…',
    },
    addManager: 'Add a manager',
    addTeam: 'Add an owning team',
    archive: 'Archive this project',
    archiveHint: 'Archived projects leave lists and pickers; their work and history stay readable.',
    title: 'Projects',
    subtitle: 'Projects group work; repositories are optional.',
    new: 'New project',
    empty: 'No projects yet.',
    key: 'Key',
    name: 'Name',
    description: 'Description',
    managers: 'Managers',
    owningTeams: 'Owning teams',
    openWork: '{{count}} open',
    defaultPolicy: 'Default completion',
    requiresReview: 'Require independent review for deliverables',
    settings: 'Settings',
    visibility: 'Visibility',
    repositories: 'Repositories',
  },
  teams: {
    title: 'Teams',
    members: 'Members',
    manager: 'Manager',
    member: 'Member',
    addMember: 'Add member',
    remove: 'Remove',
    new: 'New team',
    slug: 'Identifier',
    archive: 'Archive team',
    restore: 'Restore team',
    archived: 'Team archived',
    restored: 'Team restored',
    archivedBadge: 'Archived',
    archiveHint: 'Hides the team from lists and pickers. Move its projects, open work and open requests to another team first.',
  },
  documents: {
    title: 'Documents',
    subtitle: 'Company documentation in folders, with who views and who edits set per folder.',
    folders: 'Folders',
    moreActions: 'More actions',
    archiveLink: 'Archived',
    archiveTitle: 'Archived documents and folders',
    archiveHint: 'Archived items keep their content and history. Editors of their folder restore them.',
    archiveEmpty: 'Nothing is archived.',
    restore: 'Restore',
    restoredItem: 'Restored',
    resizeTree: 'Resize the folder panel',
    moveNotAllowed: 'You cannot move it there',
    dropToRoot: 'Drop here to make it a top-level folder',
    open: 'Open',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    changeIcon: 'Change icon',
    removeIcon: 'Remove icon',
    iconSaved: 'Icon updated',
    permissions: 'Permissions',
    renameDocument: 'Rename document',
    documentTitle: 'Document title',
    export: 'Export',
    exportPdf: 'PDF (print or save)',
    exportMarkdown: 'Markdown (.md)',
    exportHtml: 'Web page (.html)',
    exportWord: 'Word (.docx)',
    exporting: 'Preparing the export…',
    linkTitle: 'Link',
    linkTabUrl: 'Web address',
    linkTabDocument: 'Document',
    linkTabTask: 'Task',
    linkUrl: 'Address',
    searchDocuments: 'Search documents by title',
    searchTasks: 'Search tasks by title or key',
    removeLink: 'Remove link',
    applyLink: 'Apply',
    noMatches: 'No matches',
    mentionInvite: 'Invite {{email}}',
    mentionEmpty: 'Type a name, or a company email to invite someone',
    mentionInvited: '{{name}} will see the mention when they first sign in',
    insertMedia: 'Image or video',
    uploadingMedia: 'Uploading {{name}}…',
    mediaFailed: 'Could not upload {{name}}',
    mediaType: 'Only images (PNG, JPEG, GIF, WebP) and videos (MP4, WebM, MOV) go into documents',
    comments: 'Comments',
    comment: 'Comment',
    addComment: 'Add comment',
    commentPlaceholder: 'Write a comment. Use @ to mention someone.',
    reply: 'Reply',
    replyPlaceholder: 'Reply…',
    resolve: 'Resolve',
    reopen: 'Reopen',
    resolvedBy: 'Resolved by {{name}}',
    showResolved: 'Show resolved',
    detached: 'The commented text changed or was removed',
    noComments: 'Select text in the document to comment on it.',
    editComment: 'Edit',
    deleteComment: 'Delete',
    deleteCommentConfirm: 'Delete this comment?',
    edited: 'edited',
    expandFolder: 'Expand {{name}}',
    collapseFolder: 'Collapse {{name}}',
    newFolder: 'New folder',
    newSubfolder: 'New subfolder',
    newDocument: 'New document',
    untitled: 'Untitled',
    folderName: 'Folder name',
    rename: 'Rename',
    renamed: 'Renamed',
    archiveFolder: 'Archive folder',
    archiveFolderHint: 'Only empty folders can be archived. Archive or move its documents and subfolders first.',
    archiveDocument: 'Archive document',
    restoreDocument: 'Restore document',
    archived: 'Archived',
    archivedNotice: 'This document is archived. Restore it to edit it again.',
    emptyTree: 'No folders yet',
    emptyTreeHint: 'Create a folder to start organizing documents. You become its first editor.',
    emptyFolder: 'This folder is empty.',
    subfolders: 'Subfolders',
    documentsHeading: 'Documents',
    pickFolder: 'Choose a folder or a document on the left.',
    documentCount_one: '{{count}} document',
    documentCount_other: '{{count}} documents',
    restricted: 'Restricted',
    company: 'Whole company',
    canEdit: 'You can edit',
    readOnly: 'Read only',
    updatedBy: 'Edited by {{name}}',
    access: 'Access',
    accessTitle: 'Who views and who edits',
    viewers: 'Who can view',
    viewersCompany: 'Whole company',
    viewersCompanyHelp: 'Everyone who can see the parent folder (for a top-level folder, every signed-in Vammo employee).',
    viewersRestricted: 'Only the people, teams, groups and projects below',
    viewersRestrictedHelp: 'A subfolder can only narrow its parent’s audience. Editors listed here also view. Superadmins always see everything.',
    editors: 'Who can edit',
    editInherit: 'Same as the parent folder',
    editViewers: 'Everyone who can view',
    editListed: 'Only the people, teams, groups and projects below',
    editsFrom: 'Editors come from “{{folder}}”.',
    everyoneWhoViews: 'Everyone who can view edits.',
    addPrincipal: 'Add people, teams, groups or projects',
    removePrincipal: 'Remove {{name}}',
    accessSaved: 'Access updated',
    principalKind: { USER: 'Person', TEAM: 'Team', GROUP: 'Group', PROJECT: 'Project' },
    saving: 'Saving…',
    saved: 'Saved',
    unsaved: 'Unsaved changes',
    saveFailed: 'Could not save. Your changes are kept here; retrying…',
    conflictTitle: 'Someone else saved this document',
    conflictBody: 'Your latest changes were not saved because the document changed. Copy anything you want to keep, then reload to see the latest version.',
    reload: 'Reload',
    alsoEditing: 'Also editing: {{names}}',
    placeholder: 'Write here. Use the toolbar, or Markdown shortcuts such as # or -.',
    titlePlaceholder: 'Title',
    bodyLabel: 'Document content',
    lock: 'Make private draft',
    lockEdit: 'Private draft settings',
    unlock: 'Unlock',
    locked: 'Private draft',
    lockHelp: 'Only you and the people you name see a private draft. They still need access to the folder. Unlocking gives the document the folder’s permissions again.',
    lockPeople: 'People who also see the draft',
    lockAddPerson: 'Add a person',
    lockWarning: 'These people cannot see the folder, so they still cannot open the draft: {{names}}',
    lockSaved: 'Draft settings saved',
    unlocked: 'The document now has the folder’s permissions',
    history: 'History',
    historyEmpty: 'No revisions yet.',
    revisionN: 'Revision {{n}}',
    revisionKind: { CREATE: 'Created', EDIT: 'Edited', RESTORE: 'Restored' },
    restoredFrom: 'Restored from revision {{n}}',
    viaAgent: 'via {{client}}',
    restoreRevision: 'Restore this version',
    restoreConfirm: 'Restore revision {{n}}? It is saved as a new revision, so nothing is lost.',
    restored: 'Revision restored',
    adminAccesses: 'Superadmin accesses',
    adminAccessesHelp: 'Superadmins who opened this document without being part of its audience.',
    move: 'Move',
    moveTo: 'Move to folder',
    moved: 'Moved',
    topLevel: 'Top level',
    chipRestricted: 'Restricted document',
    commentOnPage: 'Comment on the document',
    pageComment: 'On the whole document',
    colors: {
      label: 'Colors',
      none: 'Default color',
      callout: 'Callout color',
      gray: 'Gray',
      red: 'Red',
      orange: 'Orange',
      yellow: 'Yellow',
      green: 'Green',
      blue: 'Blue',
      purple: 'Purple',
      pink: 'Pink',
    },
    files: {
      attach: 'Attach a file',
      download: 'Download {{name}}',
    },
    workChip: {
      insert: 'Task',
      hint: 'Search a task by key or title. Typing a key such as VAM-123 followed by a space also links it.',
      unavailable: 'This task does not exist or you cannot see it',
    },
    presence: {
      label: 'People in this document now',
      editing: '{{name}} is editing',
      viewing: '{{name}} is reading',
    },
    collab: { rejected: 'A change could not be saved, so the document reloaded with everyone’s latest version.' },
    backlinks: { title: 'Documents', mentionedIn: 'Mentioned in' },
    projectFolder: { title: 'Documentation', choose: 'Choose the documentation folder', hint: 'The folder where this project keeps its documents. People see it here when they can read it.', folder: 'Folder', none: 'No documentation folder yet.', noneOption: 'No folder' },
    person: {
      tasks: 'Tasks',
      documents: 'Documents',
      recent: 'Recent activity',
      noActivity: 'No recent tasks or documents.',
    },
    tableMenu: {
      menu: 'Table',
      rowAbove: 'Insert row above',
      rowBelow: 'Insert row below',
      columnLeft: 'Insert column left',
      columnRight: 'Insert column right',
      headerRow: 'Header row on or off',
      deleteRow: 'Delete row',
      deleteColumn: 'Delete column',
      deleteTable: 'Delete table',
    },
    blocks: {
      plainText: 'Plain text',
      diagramView: 'Diagram or code',
      code: 'Code',
      add: 'Add a block',
      basic: 'Basic',
      richBlocks: 'Blocks',
      mediaAndLinks: 'Media and links',
      text: 'Text',
      insert: 'Insert',
      callout: 'Callout',
      toggle: 'Toggle',
      toc: 'Table of contents',
      tocEmpty: 'Add headings to build the table of contents.',
      video: 'Video',
      videoHint: 'Paste a YouTube, Loom, Vimeo or Google Drive link.',
      videoUrl: 'Video link',
      insertVideo: 'Insert video',
      embedUnsupported: 'Only YouTube, Loom, Vimeo and Google Drive videos can be embedded.',
      mermaid: 'Mermaid diagram',
      diagram: 'Diagram',
      mermaidError: 'The diagram has a syntax error.',
      language: 'Code language',
      showCode: 'Show code',
      hideCode: 'Hide code',
    },
    properties: {
      title: 'Properties',
      manage: 'Manage properties',
    },
    import: {
      action: 'Import',
      menu: 'Import documents',
      title: 'Import into {{folder}}',
      intro: 'Bring documents from Word, Google Docs, Markdown or Notion into this folder. Each file becomes a document; images and the original file are stored with it.',
      formats: 'Accepted: Word (.docx), web pages (.html), Markdown (.md), CSV tables (.csv) and Notion exports (.zip).',
      notionHelp: 'Notion: in Settings, choose Export, pick “Markdown & CSV” with subpages and upload the .zip. Its folders become folders, pages become documents with their subpages, databases become tables and images move here.',
      googleHelp: 'Google Docs: choose Google Drive below to import straight from your Drive, or use File → Download → Microsoft Word (.docx) or Web page (.html, zipped) and upload that file.',
      drive: {
        button: 'Google Drive',
        hint: 'Pick Google Docs, or Word, HTML, Markdown and CSV files stored in Drive. Google asks you once to let Vammo Work open only the files you pick.',
        notConfigured: 'Importing straight from Google Drive is not set up. Ask an administrator to set up Google Drive access.',
        opening: 'Opening Google Drive…',
        waiting: 'Waiting for Google…',
        pickerTitle: 'Choose files to import',
        source: 'Google Drive',
        remove: 'Remove {{name}}',
        errors: {
          unavailable: 'Google Drive could not be reached. Check the connection and try again.',
          popupBlocked: 'The browser blocked the Google sign-in window. Allow pop-ups for this site and try again.',
          popupClosed: 'The Google sign-in window was closed before access was granted.',
          denied: 'Access to Google Drive was not granted.',
          auth: 'The Google sign-in expired or was revoked. Choose the files from Google Drive again.',
          forbidden: 'Google Drive refused access to this file. Ask its owner to share it with you, or to allow downloads.',
          notFound: 'The file was not found in Google Drive. It may have been deleted or moved.',
          quota: 'Google Drive is limiting requests right now. Wait a minute and try again.',
          tooLarge: 'The file is too large to export from Google Drive.',
          unsupported: 'This kind of Google Drive file cannot be imported.',
          network: 'The download from Google Drive failed. Check the connection and try again.',
          timeout: 'Google did not answer in time. Try again.',
          closed: 'The Google Drive session was closed. Choose the files again.',
        },
      },
      privateNote: 'Imported documents start as private drafts that only you see, so you can review them before the folder sees them.',
      choose: 'Choose files',
      fileInput: 'Files to import',
      noFiles: 'No files chosen yet.',
      start: 'Import',
      stop: 'Stop',
      progress: '{{done}} of {{total}} done',
      progressLabel: 'Import progress',
      status: {
        pending: 'Waiting',
        working: 'Importing',
        done: 'Imported',
        failed: 'Failed',
        skipped: 'Not imported',
      },
      summary_one: '{{count}} document imported into {{folder}}.',
      summary_other: '{{count}} documents imported into {{folder}}.',
      stopped: 'The import was stopped. What was already imported stays.',
      lockHint: 'Imported documents are private drafts that only you see. Review each one, then unlock it from its More actions menu to share it with the folder.',
      created: 'Imported documents',
      foldersCreated: 'Folders created',
      skippedTitle: 'Not imported',
      failedTitle: 'Failed',
      copy: 'Copy report',
      copied: 'Report copied',
      footer: 'Imported on {{date}} from',
      notes_one: '{{count}} import note',
      notes_other: '{{count}} import notes',
      issues: {
        equation: 'Equations kept as LaTeX code',
        footnote: 'Footnotes kept as plain text',
        html: 'HTML the editor does not support; its text was kept: {{detail}}',
        remoteImage: 'Image from another site kept as a link, not copied: {{detail}}',
        unsafeLink: 'Link removed because its address is not allowed: {{detail}}',
        brokenLink: 'Link removed because its target is not part of the import: {{detail}}',
        missingFile: 'File not found in the import: {{detail}}',
        embed: 'Embedded content not supported, kept as a link: {{detail}}',
        converterWarning: 'Word conversion note: {{detail}}',
        comments: 'Comments are not imported',
        rowsCapped: 'Table cut to its first rows ({{detail}})',
        fileTooLarge: 'File larger than 25 MB not uploaded: {{detail}}',
        uploadFailed: 'File could not be uploaded: {{detail}}',
        notInline: 'Image format that is not shown in documents, attached as a file: {{detail}}',
        tooDeep: 'Moved up a level: subpages nest at most 8 levels',
        plainText: 'The layout could not be kept, so the text was imported as plain paragraphs',
        originalTooLarge: 'Original larger than 25 MB not attached: {{detail}}',
        unsupportedBlock: 'Notion block not supported, left out: {{detail}}',
        columns: 'Columns were placed one after another',
        database: 'Notion database imported as a static table: {{detail}}',
        pageNotImported: 'Link to a page that was not imported kept as a Notion link: {{detail}}',
        truncated: 'Page cut after its first {{detail}} blocks',
        cellShortened: 'Database values shortened to fit: {{detail}}',
        cellDropped: 'Database values left out because they did not fit the property: {{detail}}',
      },
      tabs: { files: 'Files', notion: 'Notion' },
      notion: {
        intro: 'Import pages straight from your Notion workspace. On Notion’s own screen you choose which pages Vammo Work may read; only those can be imported.',
        connect: 'Connect Notion',
        connectHint: 'You go to Notion to choose the pages to share, then come back here.',
        connectedTo: 'Connected to {{workspace}}',
        sharePages: 'Share other pages',
        search: 'Search Notion pages',
        searchPlaceholder: 'Search by title',
        empty: 'No pages are shared with Vammo Work yet. Use “Share other pages” to choose them in Notion.',
        noMatches: 'No shared page matches.',
        loadMore: 'Load more',
        database: 'Database',
        pageList: 'Shared Notion pages',
        selected_one: '{{count}} page selected',
        selected_other: '{{count}} pages selected',
        subpagesNote: 'Subpages come along. Databases become tables of up to 500 rows. Notion files and images are copied here.',
        start: 'Import from Notion',
        queued: 'Waiting for the import to start…',
        running: 'Importing from Notion: {{done}} of {{total}} pages',
        failedRun: 'The import stopped: {{error}}',
        alreadyRunning: 'You already have a Notion import in progress. Wait for it to finish or stop it, then start another.',
        reconnectNeeded: 'Notion no longer accepts your connection. Connect Notion again.',
        openInNotion: 'Open in Notion',
        connected: 'Notion connected',
        denied: 'Notion access was not granted',
        stateError: 'The Notion connection could not be confirmed. Try again.',
        connectFailed: 'Notion could not be connected. Try again.',
        skipReasons: {
          limit: 'more than 200 pages in one import',
          notShared: 'not shared with Vammo Work',
          duplicate: 'already imported in this run',
          cancelled: 'the import was stopped first',
          timeLimit: 'the import reached its time limit first; import it again',
          interrupted: 'the import was interrupted first; import it again',
        },
      },
      skipReasons: {
        unsafePath: 'unsafe or repeated path in the zip',
        unreferenced: 'no page uses it',
        unsupportedType: 'file type not supported',
        tooLarge: 'too large',
        emptyFile: 'empty file',
        cancelled: 'the import was stopped first',
      },
      errors: {
        zipTooLarge: 'The zip holds more than {{limit}} MB once uncompressed.',
        zipTooManyFiles: 'The zip holds more than {{limit}} files.',
        zipUnreadable: 'The file is not a readable zip.',
        nothingToImport: 'The zip has no Markdown, HTML or CSV pages.',
        cancelled: 'Stopped before its content was saved; the document is empty.',
      },
    },
    subpages: {
      title: 'Subpages',
      new: 'New subpage',
      empty: 'No subpages yet.',
    },
    readers: {
      title: 'Who viewed it',
      hint: 'People who opened this document and when they last did.',
      empty: 'Nobody else has opened it yet.',
      views_one: '{{count}} view',
      views_other: '{{count}} views',
    },
    diff: {
      title: 'Compare versions',
      content: 'Content',
      changes: 'Changes',
      against: 'Changes since revision {{n}}',
      first: 'First revision: everything is new',
      noChanges: 'No text changes in this revision.',
    },
    translation: {
      label: 'Language',
      original: 'Original',
      locales: { en: 'EN', 'pt-BR': 'PT', es: 'ES' },
      reasons: {
        DISABLED: 'This deployment has no machine translator.',
        DAILY_LIMIT: 'The daily AI limit was reached; blocks not translated yet show the original.',
        RATE_LIMIT: 'Too many translation requests; new blocks translate in a minute.',
        INVALID: 'The translation could not be shown.',
      },
      pending: 'Machine translation in progress: {{done}} of {{total}} blocks ready.',
      ready: 'Machine translation, read-only. The original stays the source of truth.',
      unavailable: 'Machine translation is not available; showing the original.',
    },
    draft: {
      start: 'Edit without publishing',
      banner: 'Unpublished changes',
      bannerHint: 'Only you see these changes until you publish them.',
      stale: 'The document changed after you started this draft; publishing replaces those changes.',
      publish: 'Publish',
      discard: 'Discard changes',
      published: 'Changes published',
      staleConfirm: 'The document changed after you started this draft. Publish anyway and replace those changes?',
      discardConfirm: 'Discard your unpublished changes?',
    },
    table: {
      byPerson: 'By {{name}}',
      clearPerson: 'Show everyone’s documents',
      title: 'Documents table',
      search: 'Search by title',
      folder: 'Folder',
      allFolders: 'All folders',
      addFilter: 'Add filter',
      removeFilter: 'Remove filter',
      property: 'Property',
      operator: 'Condition',
      ops: { eq: 'is', contains: 'contains', empty: 'is empty', set: 'is set' },
      choose: 'Choose…',
      yes: 'Yes',
      no: 'No',
      document: 'Document',
      updated: 'Updated',
      empty: 'No documents match these filters.',
    },
    toolbar: {
      label: 'Formatting',
      underline: 'Underline',
      textColor: 'Text color',
      bold: 'Bold',
      italic: 'Italic',
      strike: 'Strikethrough',
      code: 'Inline code',
      heading1: 'Heading 1',
      heading2: 'Heading 2',
      heading3: 'Heading 3',
      bulletList: 'Bulleted list',
      orderedList: 'Numbered list',
      taskList: 'Checklist',
      quote: 'Quote',
      codeBlock: 'Code block',
      link: 'Link',
      linkPrompt: 'Link address (https://, mailto: or a /path)',
      linkInvalid: 'That address is not allowed',
      table: 'Insert table',
      divider: 'Divider',
      undo: 'Undo',
      redo: 'Redo',
    },
    public: {
      folderName: 'Public external',
      folderHint: 'Anyone with a document’s public link can read it without signing in. New documents here start as private drafts: unlock one to publish it.',
      badge: 'Public',
      badgeHint: 'Anyone with the public link can read this document',
      button: 'Public link',
      title: 'Public link',
      description: 'Anyone with this link can read the document without signing in, also outside Vammo. Comments, history and authors stay private.',
      disabled: 'Public links are turned off in this deployment.',
      notPublicLocked: 'This document is a private draft, so it is not public. Unlock it to publish it.',
      notPublicArchived: 'This document is archived, so its public link does not work.',
      notPublicOutside: 'Only documents in the Public external folder have a public link.',
      copy: 'Copy link',
      copied: 'Public link copied',
      open: 'Open the public page',
      rotate: 'Change link',
      rotateConfirm: 'The current link will stop working at once.',
      rotated: 'Link changed; the old one no longer works',
      passwordOn: 'Readers need a password',
      passwordOff: 'No password: the link alone is enough',
      passwordNew: 'Password',
      passwordChange: 'Change password',
      passwordSet: 'Require password',
      passwordRemove: 'Remove password',
      passwordHint: 'At least 8 characters. Share it separately from the link; it cannot be shown again.',
      passwordSaved: 'Password saved',
      passwordRemoved: 'Password removed',
      views: 'Views in the last 30 days: {{count}}',
      viewsTotal: 'all time: {{count}}',
      confirmTitle: 'Make it public?',
      confirmBody: 'Anyone with the link will be able to read it without signing in, including people outside Vammo. Comments, history and authors stay private.',
      published: 'Published: anyone with the public link can read it',
      confirmAction: 'Publish',
      restrictedNotAllowed: 'Everyone in the company sees the folders in Public external, because anything in them can be published. To limit who sees this folder, move it out of Public external first.',
      restrictedMoveRefused: 'A folder that limits who sees it (or has such a subfolder) cannot move into Public external. Set its viewers to the whole company first.',
    },
  },
  publicPage: {
    loading: 'Loading the document',
    notFound: 'This link is not available',
    notFoundHint: 'The document does not exist or is no longer public. Ask the person who shared it for a new link.',
    passwordTitle: 'This document is protected',
    passwordHint: 'Enter the password you received with the link.',
    password: 'Password',
    open: 'Open',
    wrongPassword: 'That password is not correct',
    tooMany: 'Too many attempts. Wait a few minutes and try again.',
    updated: 'Updated {{date}}',
    internalDocument: 'internal document',
    databaseNotShown: 'Database not shown publicly',
    contents: 'Contents',
    download: 'download',
    sharedFrom: 'Shared from Vammo Work',
    error: 'The document could not be loaded.',
    retry: 'Try again',
  },
  visibility: {
    title: 'Visibility',
    grants: 'People, teams and groups',
    addGrant: 'Add access',
    searchPrincipal: 'Search people, teams or groups',
    inherit: 'Restrictions from parent projects always apply.',
    changed: 'Visibility updated',
  },
  settings: {
    title: 'Settings',
    profile: 'Profile',
    displayName: 'Display name',
    language: 'Language',
    timezone: 'Time zone',
    notifications: 'Notifications',
    identities: 'Linked accounts',
    noIdentities: 'No linked accounts.',
    roles: 'Access',
    basic: 'Basic access',
    notion: {
      title: 'Notion',
      description: 'Import pages from your Notion workspace into documents. You choose the pages Vammo Work may read in Notion.',
      notConnected: 'Notion is not connected.',
      connect: 'Connect Notion',
      connectedTo: 'Connected to {{workspace}} since {{date}}',
      disconnect: 'Disconnect',
      confirmDisconnect: 'Disconnect Notion?',
      disconnected: 'Notion disconnected',
      revokeHint: 'Disconnecting deletes the access stored here. To remove Vammo Work from Notion too, open Settings → Connections in Notion.',
    },
    github: {
      addLabel: 'Add a GitHub account',
      hint: 'Your GitHub username, for example a personal account you sometimes commit with. Accounts that commit with your Vammo email are linked automatically.',
      placeholder: 'username',
      add: 'Add',
      added: 'GitHub account added',
      remove: 'Remove',
      removed: 'GitHub account removed',
      confirmRemove: 'Remove this account?',
      method: { self: 'Added by you', email: 'Verified by commit email', admin: 'Linked by an administrator', other: 'Linked' },
      errors: {
        notFound: 'No GitHub account has this username.',
        notUser: 'Only personal GitHub accounts can be added, not organizations or bots.',
        taken: 'This GitHub account is already linked to someone else. Ask an administrator.',
        tooMany: 'You already have the maximum number of GitHub accounts.',
        unavailable: 'GitHub could not be reached. Try again in a moment.',
      },
    },
  },
  admin: {
    title: 'Administration',
    users: 'Users and roles',
    groups: 'Visibility groups',
    integrations: 'Integrations',
    exceptions: 'Exceptions',
    imports: 'Jira import',
    audit: 'Audit log',
    operations: 'Operations',
    roles: 'Roles',
    capabilities: 'Capabilities',
    roleHints: { SUPERADMIN: 'Full control. Reads every record, including restricted folders, locked drafts and sensitive reports. Only a Superadmin appoints Superadmins.', ADMIN: 'Manages people, teams, groups, connectors, operations and imports. Reads only what their own access allows.', DEVELOPER: 'Engineering work and technical reports.', PRODUCT: 'Triage and product work.' },
    status: 'Status',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    lastLogin: 'Last sign-in',
    agents: { column: 'AI agents', none: 'None', lastSeen: 'Last connected {{when}}', via: { PLUGIN: 'Plugin', MCP: 'MCP only', UNKNOWN: 'Unconfirmed' }, viaHint: { PLUGIN: 'Plugin {{version}} installed', MCP: 'MCP server configured without the plugin', UNKNOWN: 'Used before the tracker recorded how it connects; confirmed at its next connection' }, filter: 'Agent connection', filters: { ALL: 'All', PLUGIN: 'Plugin installed', MCP: 'MCP only', NONE: 'No agent' } },
    changeAccess: 'Change access',
    reasonRequired: 'A reason is recorded in the audit log.',
    reason: { title: 'Record a reason', label: 'Reason', hint: 'The reason is stored in the audit log.', confirm: 'Confirm' },
    integ: { lastSuccess: 'Last success', lastAttempt: 'Last attempt', lastError: 'Last error', circuit: 'Circuit open until', cursor: 'Cursor', probe: 'Probe', reconcile: 'Reconcile', reconcileQueued: 'Reconciliation queued', liveVerified: 'Live verified', health: { HEALTHY: 'Healthy', DEGRADED: 'Degraded', FAILING: 'Failing', DISABLED: 'Disabled', UNVERIFIED: 'Unverified' }, modes: { live: 'Live', fixture: 'Fixture', disabled: 'Disabled' } },
    deliveries: { title: 'Notification deliveries', hint: 'Uncertain sends may already have reached the person. Check provider evidence before retrying.', filter: 'Delivery status', filters: { DELIVERY_UNKNOWN: 'Uncertain or failed', BOUNCED: 'Bounced or suppressed', SENT: 'Sent', ALL: 'All' }, empty: 'No deliveries need attention.', attempts: '{{count}} attempt(s)', retry: 'Retry', retryTitle: 'Retry this delivery', retryHint: 'The message may be sent again. The reason is stored in the audit log.', retried: 'Retry queued' },
    services: { title: 'Services and environments', hint: 'Approve which repositories build each service and which environments are production. Coverage uses only approved mappings.', empty: 'No services discovered yet.', repositories: 'Repositories', environments: 'Environments', buildPath: 'Build path (optional)', buildPathFor: 'Build path for {{name}}', classificationFor: 'Classification of {{name}}', noRepositories: 'No repositories mapped.', approve: 'Approve mapping', approved: 'Mapping approved', classes: { PRODUCTION: 'Production', STAGING: 'Staging', DEVELOPMENT: 'Development', PREVIEW: 'Preview', OTHER: 'Other' } },
    identities: { action: 'Linked accounts', title: 'Linked accounts', hint: 'Link a GitHub or Jira account only when you have confirmed it belongs to this person. Links are audited.', none: 'No linked accounts.', add: 'Link an account', provider: 'Provider', search: 'Search by handle or ID', candidates: 'Accounts that can be linked', historical: 'Jira history', unmapped: 'Seen {{count}} time(s)', noCandidates: 'No unlinked accounts found.', link: 'Link account', linked: 'Account linked', unlink: 'Unlink' },
    exc: { empty: 'No open exceptions.', resolve: 'Resolve', ignore: 'Ignore', open: 'Open', occurrences: '{{count}} occurrence(s)', filterKind: 'Kind', all: 'All kinds', kinds: { UNMATCHED_WORK: 'Unmatched work', UNCERTAIN_ASSOCIATION: 'Uncertain association', UNMAPPED_IDENTITY: 'Unmapped identity', UNMAPPED_REPOSITORY: 'Unmapped repository', UNMAPPED_SERVICE: 'Unmapped service', CONNECTOR_FAILURE: 'Connector failure', DUPLICATE_CORRECTION: 'Duplicate correction', WEBHOOK_INTEGRITY: 'Webhook integrity', DEPLOYMENT_ORDERING: 'Deployment ordering', DELIVERY_UNKNOWN: 'Uncertain delivery', IMPORT_EXCEPTION: 'Import exception', ACL_QUARANTINE: 'Access quarantine', SCOPE_REVIEW: 'Scope review', COMPLETION_BLOCKED: 'Completion blocked' } },
    ops: { noAlerts: 'No active alerts', outbox: 'Outbox pending', worker: 'Worker', healthy: 'Healthy', stale: 'Stale', aiTokens: 'AI tokens today', queue: 'Queue', queued: 'Queued', active: 'Active', oldest: 'Oldest wait', dead: 'Dead letters', replay: 'Replay', deadEmpty: 'No dead-letter jobs.' },
    imp: { mode: 'Mode', projects: 'Project keys (comma separated; empty uses approved projects)', start: 'Start', queued: 'Import queued', resume: 'Resume', cancel: 'Cancel', empty: 'No import runs yet.', exceptions: '{{count}} exception(s)', mapping: 'Mappings', mappingHelp: 'Run discovery first. Approve each project before sample, full or delta imports. Unmapped security levels stay quarantined for administrators.', save: 'Save mappings', saved: 'Mappings saved', approved: 'Approved', targetProject: 'Target project', targetTeam: 'Triage/responsible team', none: 'None', requestTypes: 'Issue types imported as requests', statusMap: 'Status mapping', securityLevels: 'Security levels', securityGroup: 'Visible to group', quarantine: 'Quarantine (administrators only)', reconciliation: 'Reconciliation', modes: { DISCOVERY: 'Discovery', DRY_RUN: 'Dry run', SAMPLE: 'Sample', FULL: 'Full import', DELTA: 'Delta', RECONCILE: 'Reconcile' }, statuses: { QUEUED: 'Queued', RUNNING: 'Running', PAUSED: 'Paused', SUCCEEDED: 'Succeeded', FAILED: 'Failed', CANCELED: 'Canceled' } },
    slackDest: { title: 'Slack progress destinations', help: 'Approved internal channels that receive progress for a project or team. Restricted items post only a link unless the destination is approved for restricted content and every member is a full workspace member.', scope: 'Scope', project: 'Project', team: 'Team', workspace: 'Workspace ID', channel: 'Channel ID', restricted: 'Approved for restricted content', milestones: 'Milestones', add: 'Save destination', remove: 'Remove', empty: 'No destinations configured.', saved: 'Destination saved', milestoneNames: { REQUEST_RECEIVED: 'Request received', REQUEST_ACCEPTED: 'Request accepted', REQUEST_REJECTED: 'Request rejected', REQUEST_RESOLVED: 'Request resolved', REQUEST_NEEDS_ATTENTION: 'Needs attention', WORK_CREATED: 'Task created', WORK_STARTED: 'Task in progress', WORK_IN_REVIEW: 'Task in review', WORK_BLOCKED: 'Task blocked', WORK_INTEGRATED: 'Task merged', WORK_DEPLOYED: 'Deployed to production', WORK_DELIVERED: 'Task completed', WORK_CANCELED: 'Task canceled' } },
  },
  reports: {
    notes: {
      sourceNotConnected: '{{source}} is not connected; values from it are unknown, not zero.',
      sourceNotLive: '{{source}} data comes from {{mode}} mode, not live evidence.',
      sourceNeverSynced: '{{source}} has not synchronized successfully yet.',
      duplicatesIncluded: '{{count}} of these requests are marked as duplicates.',
      reconsideredExcluded: '{{count}} reconsidered decisions in the period are excluded from the rate.',
      generationsAcrossRequests: '{{generations}} fulfillment generations across {{requests}} distinct requests.',
      mixedBases: 'Mixed completion bases ({{bases}}); choose one to compare like with like.',
      partialCohorts: 'The follow-up window runs from each first completion until now; recent cohorts are partial.',
      unattributedBugs_one: '{{count}} visible bug created in the period, whatever the filters, has no cause recorded; already closed: {{closed}}.',
      unattributedBugs_other: '{{count}} visible bugs created in the period, whatever the filters, have no cause recorded; already closed: {{closed}}.',
      noDeliveryCause_one: '{{count}} visible bug created in the period, whatever the filters, has no delivery cause: {{breakdown}}.',
      noDeliveryCause_other: '{{count}} visible bugs created in the period, whatever the filters, have no delivery cause: {{breakdown}}.',
      openWindow_one: '{{count}} item deployed less than {{days}} days ago can still collect defects in this period.',
      openWindow_other: '{{count}} items deployed less than {{days}} days ago can still collect defects in this period.',
      caughtBeforeRelease_one: '{{count}} linked bug was found before its origin was deployed and is excluded.',
      caughtBeforeRelease_other: '{{count}} linked bugs were found before their origin was deployed and are excluded.',
      outsideWindow_one: '{{count}} linked bug was found more than {{days}} days after its origin was deployed and is excluded.',
      outsideWindow_other: '{{count}} linked bugs were found more than {{days}} days after their origin was deployed and are excluded.',
      originNotDeployed_one: '{{count}} linked bug names work with no “{{basis}}” completion and is excluded.',
      originNotDeployed_other: '{{count}} linked bugs name work with no “{{basis}}” completion and are excluded.',
      currentState: 'Current state at query time; past snapshots are not reconstructed for this metric.',
      rolledBack: '{{count}} rolled-back deployments are labeled separately.',
      noIncidentSource: 'No incident source is connected; recovery time is unavailable and never inferred from deployments.',
      activityNotProductivity: 'Contributions are activity indicators, not a productivity score.',
      prAttribution: 'Pull request authorship is labeled per pull request, not per line. Generated-file exclusions apply only with complete file data.',
      captureDenominator: 'Denominator: observed pull requests in repositories attached to projects; work outside the platform has no denominator.',
      providerErrors: '{{count}} provider calls failed.',
      weightCoverage: '{{scored}} scored, {{unscored}} unscored (insufficient information, pending or failed). Not eligible: {{historical}} historical, {{restricted}} restricted, {{platform}} platform records, {{parent}} parents.',
      mixedQuestionVersions: 'Scores from {{count}} question versions are mixed in this period.',
      weightingNotLive: 'Work weighting is {{mode}}; points are not live model judgments.',
    },
    filters: { any: 'Any', source: 'Source', mine: 'Only my contributions', mineCauses: 'Only work assigned to me', person: 'Person', causeAssignee: 'Assignee of the cause' },
    defects: {
      open: 'Show the defects behind this number',
      title: 'Escaped defects · {{group}}',
      unitBug: 'Each row is one bug, with every cause that counts it here.',
      unitPair: 'Each row is one bug and one cause: a bug with several causes in this group appears once per cause, as in the count.',
      bug: 'Bug',
      created: 'Created',
      cause: 'Caused by',
      teamProject: 'Team · project',
      days: 'After deployment',
      daysValue_one: '{{count}} day',
      daysValue_other: '{{count}} days',
      showing_one: 'Showing {{shown}} of {{total}} defect.',
      showing_other: 'Showing {{shown}} of {{total}} defects.',
      empty: 'No defects match this number any more.',
      emptyHint: 'The data changed since the count; refresh the report.',
    },
    chartLabel: 'Chart of {{metric}} by group',
    title: 'Reports',
    subtitle: 'Metrics computed from recorded facts, with your access applied. Unknown data is never shown as zero.',
    metric: 'Metric',
    period: 'Period',
    groupBy: 'Group by',
    basis: 'Completion basis',
    anyBasis: 'All bases',
    firstOnly: 'First completion only',
    run: 'Run',
    export: 'Export CSV',
    exporting: 'Preparing export…',
    download: 'Download CSV',
    total: 'Total',
    count: 'Count',
    unavailable: 'Unavailable',
    median: 'Median',
    p85: '85th percentile',
    coverage: 'Coverage',
    overlapping: 'Groups overlap: one task can appear in several groups, so rows do not add up to the total.',
    empty: 'No data in this period.',
    tierLabel: 'Tier',
    formula: 'Formula v{{version}}',
    watermark: 'Data as of {{time}}',
    definition: 'Definition',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    last90: 'Last 90 days',
    thisYear: 'This year',
    from: 'From',
    to: 'To',
    group: 'Group',
    value: 'Value',
    ratio: '{{num}} of {{den}}',
    tiers: { GENERAL: 'General', TECHNICAL: 'Technical', SENSITIVE: 'Sensitive' },
    coverageStatus: { COMPLETE: 'Complete', PARTIAL: 'Partial', UNKNOWN: 'Unknown' },
    groups: { none: 'No grouping', team: 'Team', project: 'Project', type: 'Type', priority: 'Priority', week: 'Week', month: 'Month', repository: 'Repository', service: 'Service', person: 'Person', outcome: 'Outcome', source: 'Source' },
    metrics: {
      requests_created: { name: 'Requests created', help: 'Submitted requests by submission date.' },
      request_first_response: { name: 'First response time', help: 'From submission to the first human triage decision.' },
      request_acceptance_rate: { name: 'Acceptance rate', help: 'First accepted decisions over accepted plus rejected.' },
      requests_fulfilled: { name: 'Requests resolved', help: 'Distinct requests with a resolution in the period.' },
      request_lead_time: { name: 'Request lead time', help: 'From submission to resolution, per resolution.' },
      work_completed: { name: 'Work completed', help: 'Distinct work items completed in the period.' },
      work_cycle_time: { name: 'Cycle time', help: 'From start to completion of each episode.' },
      work_in_progress: { name: 'Work in progress', help: 'Items in progress, review or integrated right now.' },
      blocked_time: { name: 'Blocked time', help: 'Union of blocker intervals per item in the period.' },
      review_wait: { name: 'Review wait', help: 'From review request to review decision.' },
      integration_to_production: { name: 'Integration to production', help: 'From integration to verified production delivery.' },
      deployment_frequency: { name: 'Deployment frequency', help: 'Successful production deployments.' },
      deployment_failure_rate: { name: 'Deployment failure rate', help: 'Failed over successful plus failed production attempts.' },
      reopen_rate: { name: 'Reopen rate', help: 'Completed work that was later reopened.' },
      escaped_defects: { name: 'Escaped defects', help: 'Bugs found within a fixed window after deploying the work that caused them.' },
      recovery_time: { name: 'Recovery time', help: 'Needs incident evidence; unavailable until a source exists.' },
      contribution_activity: { name: 'Contributions', help: 'Observed implementation, review and other contributions.' },
      code_additions: { name: 'Lines added', help: 'Provider-reported additions in merged PRs.' },
      code_deletions: { name: 'Lines deleted', help: 'Provider-reported deletions in merged PRs.' },
      capture_coverage: { name: 'Capture coverage', help: 'Observed PRs linked to confirmed work.' },
      requester_confirmation: { name: 'Requester confirmation', help: 'Confirmed, persists or unanswered per resolution.' },
      integrated_ai_usage: { name: 'AI usage', help: 'Provider-reported tokens for translation, intake and work weighting.' },
      weighted_work_completed: { name: 'Weighted work completed', help: 'Points of completed leaf items, weighed by TypeSafe Jev (1, 2, 3, 5, 8 or 13 each).' },
    },
  },
  attachments: {
    title: 'Files',
    add: 'Attach files',
    empty: 'No files attached yet.',
    uploading: 'Uploading {{name}}…',
    scanning: 'Scanning for malware',
    rejected: 'Rejected',
    download: 'Download',
    remove: 'Remove file',
    removed: 'File removed',
    tooLarge: '{{name}} is larger than {{max}} MiB.',
    tooMany: 'You can attach at most {{max}} files at once.',
    uploaded: '{{count}} file(s) uploaded; they become available after the malware scan.',
    pendingNote: 'Files stay quarantined until the scan finishes.',
    video: 'Video',
    view: 'View {{name}}',
    viewer: 'File viewer',
    viewerHint: 'Use the arrow keys to move between files and Escape to close.',
    counter: '{{index}} of {{total}}',
    previous: 'Previous file',
    next: 'Next file',
    actualSize: 'Actual size',
    fitToScreen: 'Fit to screen',
    openInNewTab: 'Open in a new tab',
    loadFailed: 'This file cannot be shown here. Download it to open it on your device.',
  },
  savedViews: {
    title: 'Views',
    mine: 'My views',
    sharedWithEveryone: 'Shared views',
    shared: 'Shared',
    empty: 'No saved views yet.',
    saveCurrent: 'Save current filters…',
    name: 'View name',
    shareLabel: 'Share with everyone (filters only; each person still sees only what they can access)',
    hint: 'A saved view stores the filters, never the results.',
    saved: 'View saved',
    deleted: 'View deleted',
    delete: 'Delete view',
  },
  slack: {
    modalTitle: 'Create Vammo request',
    reviewTitle: 'Review request',
    next: 'Next',
    submit: 'Submit',
    cancel: 'Cancel',
    close: 'Close',
    privacyNote: 'Only you see this form. Your request is private until you submit it.',
    fields: { title: 'Short title', problem: 'What is happening?', expected: 'What should happen instead?', impact: 'Who is affected and how much? (leave empty if unknown)' },
    impactUnknown: 'Unknown for now',
    duplicates: 'Possibly related requests',
    submitted: 'Request *{{key}}* submitted. Follow it here: {{url}}',
    dmHelp: 'Write what you need in a message and I will turn it into a Vammo request draft that you review before sending. You can also use /vammo-work or the "Create Vammo request" shortcut on any message.',
    dmDraftReady: 'I prepared a request draft: *{{title}}*. Review it, complete it and send it:',
    dmReview: 'Review and send',
    linkPrompt: 'To create requests from Slack, link your Slack account to your Vammo account (private link, valid for 10 minutes): {{url}}',
    notLinked: 'Your Slack account is not linked yet. Run /vammo-work again.',
    expired: 'This draft is no longer available. Start again with /vammo-work.',
    required: 'Required',
    unavailable: 'The request form could not be opened. Try again or use the web app.',
    progress: '*{{key}}* {{title}} — {{status}}\n{{url}}',
    progressLinkOnly: 'A tracked item was updated. Open it in Vammo Work: {{url}}',
    linkTitle: 'Link Slack account',
    linkBody: 'Link the Slack user {{handle}} to your Vammo account? Requests you create from Slack will be filed as you.',
    linkConfirm: 'Link account',
    linkDone: 'Your Slack account is linked. You can close this tab and run /vammo-work again.',
    linkInvalid: 'This link expired or was already used. Run /vammo-work in Slack to get a new one.',
  },
  notifications: {
    preferences: {
      title: 'Notification settings',
      event: 'Event',
      email: 'Email',
      browser: 'Browser',
      immediate: 'Immediately',
      digest: 'Daily digest',
      muted: 'Off',
      on: 'On',
      off: 'Off',
    },
    events: {
      REQUEST_RECEIVED: 'Your request was received',
      REQUEST_NEEDS_INFORMATION: 'More information is needed',
      REQUEST_ACCEPTED: 'Your request was accepted',
      REQUEST_REJECTED: 'Your request was not accepted',
      REQUEST_WORK_STARTED: 'Work started on your request',
      REQUEST_RESOLVED: 'Your request was resolved',
      REQUEST_NEEDS_ATTENTION: 'A request needs attention',
      WORK_ASSIGNED: 'Work assigned to you',
      WORK_BLOCKED: 'Work you follow is blocked',
      COMMENT_ADDED: 'New comment on something you follow',
      MENTIONED: 'You were mentioned',
      WORK_DUE_SOON: 'Work you do is due soon',
      WORK_OVERDUE: 'Work you do is overdue',
      WORK_STALE: 'Work you do has had no activity',
      WORK_AUTOMATION: 'An automation rule sent you a notice',
    },
    browser: {
      title: 'Browser notifications',
      enable: 'Turn on in this browser',
      disable: 'Turn off in this browser',
      test: 'Send a test',
      enabled: 'Browser notifications are on',
      disabled: 'Browser notifications are off in this browser',
      testSent: 'Test notification sent',
      testNone: 'No browser accepted the test. Turn notifications off and on again.',
      blockedHelp: 'Notifications are blocked for this site. Allow them in the browser’s site settings (the icon next to the address).',
      devices: 'Browsers that receive notifications',
      unknownDevice: 'Browser',
      forget: 'Forget',
      state: {
        unsupported: 'This browser cannot show notifications. On iPhone and iPad, add the tracker to the Home Screen first.',
        blocked: 'Notifications are blocked for this site. Allow them in the browser’s site settings, then reload.',
        off: 'Get assignments, mentions and updates on what you follow, even when the tracker is closed.',
        on: 'This browser shows your notifications.',
      },
    },
  },
  push: {
    testTitle: 'Notifications are on',
    testBody: 'This browser will show your Vammo Work notifications.',
  },
  email: {
    greeting: 'Hello {{name}},',
    open: 'Open in Vammo Work',
    footer: 'You receive this because you are involved in this record. Change notification settings in your profile.',
    actionConfirm: 'Yes, it is resolved',
    actionPersists: 'No, it is still happening',
    actionComment: 'Leave a comment',
    confirmHint: 'Let the team know whether it works for you: open the request and choose "Yes, it is resolved" or "No, it is still happening". Opening this email or link never records an answer by itself.',
    digestSubject: 'Your Vammo Work digest',
    layout: {
      kindRequest: 'Request',
      kindWork: 'Work item',
      kindDocument: 'Document',
      openRequest: 'Open the request',
      openWork: 'Open the work item',
      openDocument: 'Open the document',
      openMyWork: 'Open My work',
      manageNotifications: 'Manage your notifications',
      actionNeeded: 'Action needed',
      update: 'Update',
      digestHeadline: 'Your daily summary',
      digestIntro: 'Here is what happened on the records you follow ({{count}}).',
    },
    events: {
      REQUEST_RECEIVED: { subject: '[{{key}}] We received your request', headline: 'We received your request', body: 'Your request "{{title}}" was received and is waiting for triage.' },
      REQUEST_NEEDS_INFORMATION: { subject: '[{{key}}] More information needed', headline: 'The team needs more information', body: 'The team needs more information about "{{title}}".' },
      REQUEST_ACCEPTED: { subject: '[{{key}}] Your request was accepted', headline: 'Your request was accepted', body: 'Your request "{{title}}" was accepted. Acceptance is not a delivery date; you will hear when work starts.' },
      REQUEST_REJECTED: { subject: '[{{key}}] Your request was not accepted', headline: 'Your request was not accepted', body: 'Your request "{{title}}" was not accepted. Open it to read the reason.' },
      REQUEST_WORK_STARTED: { subject: '[{{key}}] Work started', headline: 'Work started on your request', body: 'Work has started on "{{title}}".' },
      REQUEST_RESOLVED: { subject: '[{{key}}] Resolved — please confirm', headline: 'Your request was resolved', body: 'The team resolved "{{title}}".' },
      REQUEST_NEEDS_ATTENTION: { subject: '[{{key}}] Needs attention', headline: 'A request needs your attention', body: 'The requester reports that "{{title}}" still happens, or required delivery was lost.' },
      WORK_ASSIGNED: { subject: '[{{key}}] Assigned to you', headline: 'New work assigned to you', body: '"{{title}}" was assigned to you.' },
      WORK_BLOCKED: { subject: '[{{key}}] Blocked', headline: 'Work you follow is blocked', body: '"{{title}}" is blocked.' },
      WORK_DONE: { subject: '[{{key}}] Done', headline: 'Work you follow is done', body: '"{{title}}" is done.' },
      COMMENT_ADDED: { subject: '[{{key}}] New comment', headline: 'New comment', body: 'There is a new comment on "{{title}}".' },
      MENTIONED: { subject: '[{{key}}] You were mentioned', headline: 'You were mentioned', body: 'You were mentioned on "{{title}}".' },
      WORK_DUE_SOON: { subject: '[{{key}}] Due soon', headline: 'Work is due soon', body: '"{{title}}" is due soon.' },
      WORK_OVERDUE: { subject: '[{{key}}] Overdue', headline: 'Work is overdue', body: '"{{title}}" is past its due date.' },
      WORK_STALE: { subject: '[{{key}}] No recent activity', headline: 'Work with no recent activity', body: '"{{title}}" has not changed in a while. Update it, or close it if it is no longer needed.' },
      WORK_AUTOMATION: { subject: '[{{key}}] Automation notice', headline: 'An automation rule sent you a notice', body: 'An automation rule sent you a notice about "{{title}}". Open it to see the message.' },
    },
  },
  tutorial: {
    title: 'Tutorial',
    subtitle: 'Connect your AI assistant, ask for what you need, plan work with your team and let code work track itself.',
    toc: 'On this page',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy; select the text and copy it manually.',
    footer: 'Everything you or your assistant does here runs with your own permissions.',
    intro: {
      title: 'What the tracker is for',
      lead: 'Vammo Work is where all of Vammo’s work lives: what people ask for, the tasks teams carry out and the code that delivers them. The idea is simple: **your work tracks itself**. You, or your AI assistant, do the work; the tracker records it, links it to the right task and follows it until it is running in production. Everyone sees what is happening without asking around or updating tickets by hand.',
      requests: { title: 'Requests', body: 'Anyone describes a need or a problem (`REQ-` keys) and follows it until they confirm it is solved.' },
      work: { title: 'Work', body: 'Teams turn needs into tasks (`VAM-` keys) and follow them in lists and boards, by team, project or person.' },
      agents: { title: 'Your assistant keeps it up to date', body: 'With the Vammo plugin, Claude or Codex open the task, move it to in progress, record milestones and link branches, commits and pull requests while you code.' },
      delivery: { title: 'Delivery is verified', body: 'Work is done when the tracker sees its code deployed in production, not when someone says so.' },
      start: 'First step: **connect your AI assistant** below. It takes a couple of minutes and is what makes tracking automatic.',
    },
    connect: {
      title: 'Connect your AI assistant',
      intro: 'The tracker is an MCP server. Once it is connected, your assistant can file requests, create and update work, and record what it does — always as you and with your permissions. You sign in with your Vammo account; there is no API key to copy.',
      serverUrl: 'Server URL',
      pluginTitle: 'What gets installed?',
      plugin: 'The Codex and Claude Code **plugin** adds the MCP connection and a skill that guides the assistant to track work. Claude Code also gets a session reminder. The ChatGPT custom MCP setup creates a plugin from the remote MCP server, with tools and server instructions. Tracking happens when the assistant calls those tools; installation alone does not record every conversation.',
      claudeDesktop: {
        name: 'Claude (web and desktop)',
        summary: 'Claude chat on claude.ai and in the Windows and macOS app. With your Vammo company account the plugin is already installed; you only connect it once.',
        s1: 'In Claude, open **Customize → Plugins** and check that **Vammo Work Tracker** is there. It comes installed with your company account.',
        s2: 'Open the plugin, go to its **Connectors** tab and choose **Connect**. Vammo sign-in opens in your browser: sign in with your Vammo Google account and approve the access requested.',
        s3: 'Set the tool permissions of the connector: **Always allow** for the tools that read and track work (`get_…`, `search_…`, `ensure_work_item`, `start_work_session`, `link_code`, `record_progress`, `finish_work_session`, `add_comment`…). Keep **Needs approval** for `write_operation`, which can do anything you can do in the web app, including deleting.',
        s4: 'In a new chat, open the tools menu (**+**) and check that **Vammo Work Tracker** is turned on. The plugin’s skill needs **code execution**: if Claude does not follow it, turn code execution on in **Settings → Capabilities**.',
        s5: 'Using a personal Claude account instead? The plugin is not there: open **Settings → Connectors → Add custom connector**, name it `Vammo Work Tracker`, paste the server URL, connect and set the permissions as in step 3:',
        s6: 'Optional: paste these instructions into a Claude project so it always works with the tracker:',
        instructions: 'Use the Vammo Work Tracker connector for requests and work. Call get_context first. To file a request, call prepare_request with what I describe, show me the draft and missing fields, and only call submit_request after I confirm. When a request I filed is resolved, ask me whether it is fixed and record confirm_request. Never invent facts, priorities or deployment status.',
      },
      claudeCode: {
        name: 'Claude Code (terminal)',
        summary: 'Claude in your terminal, your IDE or the Code tab of the desktop app. With your Vammo company account, Claude Code installs the plugin by itself when it starts; it brings the server, the work-tracking skill and a reminder, so coding work is tracked automatically in every repository.',
        s1: 'Using a personal Claude account? Install the plugin once (company accounts skip this step):',
        s2: 'Restart Claude Code, run `/mcp`, select `plugin:vammo-work-tracker:vammo` and choose **Authenticate**. Sign in with your Vammo Google account in the browser. You do this once per computer.',
        s3: 'The first time Claude uses each Vammo tool it asks for permission. Answer **Yes, and don’t ask again** for the tools that read and track work; keep being asked for `write_operation`, which can do anything you can do in the web app.',
        s4: 'If you set the tracker up by hand before, run `claude mcp remove vammo` and delete `~/.claude/skills/vammo-work-tracking/`, so tools are not duplicated.',
        s5: 'Keep the plugin up to date. Company accounts update it automatically; on a personal account, run this and restart Claude Code:',
      },
      chatgptDesktop: {
        name: 'ChatGPT (web)',
        summary: 'Start at chatgpt.com with the workspace where you will use the tracker. If Vammo Work Tracker is already available in Plugins, install it and connect your account.',
        s1: 'Open **Plugins**, choose **+**, then **Add custom MCP server**.',
        s2: 'Name it **Vammo Work Tracker**, paste the URL and choose **OAuth**. If asked for a registration method, choose **Dynamic client registration (DCR)**. No API key or manually supplied client secret is needed:',
        s3: 'Review the access notice, choose **Create as a plugin**, then **Install**. When prompted to connect, sign in with your Vammo Google account and approve access.',
        s4: 'In a new chat, type **@** and select **Vammo Work Tracker**. Ask it to call `get_context` and check that it returns your name.',
        note: 'Menu names and availability depend on your workspace and version. If custom MCP servers are unavailable, ask the workspace owner to enable them. Set up and verify on the web first. Tool approval prompts depend on ChatGPT settings and are separate from signing in.',
      },
      codex: {
        name: 'Codex (CLI and IDE)',
        summary: 'Use an up-to-date Codex CLI on the same computer and user profile as your IDE. Installation and Vammo sign-in are separate steps. These commands were checked with Codex CLI 0.162.0.',
        s1: 'Install the plugin once. It adds the server and work-tracking skill without requiring access to a private GitHub repository:',
        s2: 'Connect your Vammo account. In the browser, sign in with Vammo Google and approve access. Wait for the terminal to confirm success:',
        s3: 'Start a new Codex session. If using the IDE extension, restart it so it loads the plugin and connection.',
        s4: 'Check that `vammo` is enabled and uses OAuth, then run the connection check below. The list alone does not prove that sign-in works:',
        maintenance: 'Updates and previous manual installations',
        update: 'Refresh the marketplace when a plugin update is available, check the installed version in `codex plugin list`, and restart Codex:',
        migration: 'If tools appear twice after a previous manual setup, back up your configuration. Once the plugin works, remove only the old manual `[mcp_servers.vammo]` entry and the duplicate skill you previously copied. Keep the plugin files.',
      },
      verifyTitle: 'Check the connection',
      verify: 'Ask: “Use Vammo Work Tracker to call get_context and tell me my name and permissions.” Confirm that a tool call succeeds with your account. A configured server or an installed plugin alone does not verify the connection.',
      sessionTitle: 'Stay signed in',
      session: 'The client renews access in the background. The tracker allows 90 days between renewals, so normal use keeps the connection active even when you close the browser. Sign in again on a new device or profile, after credentials are removed, after 90 days without renewal, or after access is revoked. Suspended accounts cannot connect. Approving a tool action is separate from signing in.',
      troubleTitle: 'If the connection fails',
      trouble: 'If tools are missing, restart Codex or refresh the plugin in ChatGPT and try get_context again. For an authentication error, run `codex mcp login vammo` or reconnect the ChatGPT plugin once. Repeated sign-in requests every few minutes are a fault: report the client version, time and error to the tracker administrator. Do not send tokens or sign-in callback URLs.',
      adminNote: 'Your workspace must allow custom MCP connections to this server URL. Connecting ChatGPT does not sign in a separate Codex installation.',
    },
    request: {
      title: 'Ask for a new feature',
      intro: 'Anyone can ask for a feature or report a problem. You do not need to know how to build it: describe what you need and why. Each request gets a `REQ-` key.',
      web: { title: 'From the web', body: 'Choose **New request** or press `R`. The **Guided** tab asks you questions in plain language; the **Form** tab lets you fill in the fields directly. Drafts stay private until you submit them.' },
      assistant: { title: 'From Claude or ChatGPT', body: 'Say, for example: “File a request: riders want to see their swap history in the app.” The assistant prepares a draft, shows you what is missing and submits only after you confirm.' },
      slack: { title: 'From Slack', body: 'Run `/vammo-work` or use the **Create Vammo request** shortcut on any message. The first time, Slack sends you a private link to connect your account.' },
      tipsTitle: 'What makes a good request',
      t1: 'What happens today, and what should happen instead.',
      t2: 'Who is affected and how much it matters — “I do not know yet” is a valid answer.',
      t3: 'Screenshots, links or examples as attachments.',
      after: 'Triage accepts the request and links it to work, and you are notified as it moves. When it is delivered you are asked to confirm whether it solved your problem.',
    },
    team: {
      title: 'Create tasks for your team',
      intro: 'Work items (`VAM-` keys) are the tasks a team carries out. A request describes a need; a work item describes what will be done about it.',
      s1: 'Choose **New work item** or press `C`.',
      s2: 'Write a short title and pick the type: feature, bug, maintenance, research and so on.',
      s3: 'In **Responsible**, choose the team (or **Personal** for your own work). Optionally set an assignee, a project and a priority.',
      s4: 'Add acceptance criteria: the observable results that mean the task is done.',
      s5: 'To act on a request, open it and choose **New work item** there, so the requester follows progress automatically.',
      s6: 'Follow progress in **Work** as a list or a board, filtered by team, project or assignee.',
      assistantTitle: 'Or ask your assistant',
      assistant: '“Create a task for the Platform team to add a filter to the swap history, high priority.” The assistant tells you what it will create and asks for confirmation first.',
    },
    notifications: {
      title: 'Turn on notifications',
      intro: 'Find out when your requests move, when work is assigned to you or when someone mentions you, without keeping the tracker open. Everything also appears in the notification list inside the tracker.',
      open: 'Notification settings',
      s1: 'Open **Settings → Notifications**, or choose the bell at the top right.',
      s2: 'Under **Browser notifications**, choose **Turn on in this browser** and accept when the browser asks for permission.',
      s3: 'Choose **Send a test** to check it. Repeat on each computer or phone where you want notifications; the same card lists your browsers so you can forget old ones.',
      s4: 'In **Notification settings**, choose for each event: email **Immediately**, **Daily digest** or **Off**, and browser **On** or **Off**.',
      browser: { title: 'Browser', body: 'Pop-up notifications on this device, even when the tracker tab is closed. They are turned on per browser and per device.' },
      email: { title: 'Email', body: 'On by default for your requests, assignments and mentions. **Daily digest** groups the day’s updates into a single email.' },
      slack: { title: 'Slack', body: 'Requests filed from Slack get their progress in the same thread. To also post a project’s updates in a channel, see **Use the tracker from Slack** below.' },
      troubleTitle: 'If notifications do not arrive',
      blocked: 'If the browser blocked them, allow notifications in the site settings (the icon next to the address) and reload the page.',
      iphone: 'On iPhone or iPad, first add the tracker to the Home Screen (**Share → Add to Home Screen**) and turn them on from that icon.',
      missing: 'If you do not see **Browser notifications** in Settings, they are not enabled in this environment yet; email and the in-app list still work.',
    },
    slack: {
      title: 'Use the tracker from Slack',
      intro: 'Follow a project’s updates in a Slack channel, and ask @leonardo to create and look up work without leaving the conversation.',
      channelTitle: 'Post a project’s updates in a channel',
      c1: 'In Slack, open the channel and invite the app. Without this it cannot post there, and private channels do not even appear in the list:',
      c2: 'In the tracker, open the project, choose **Settings** and go to **Slack updates**. Pick the channel from the list of channels the app can see.',
      c3: 'Choose which updates to post: tasks created, in progress, in review, blocked, merged, **deployed to production**, completed or canceled, and request milestones. A release channel, for example, can follow only **Deployed to production**.',
      c4: 'Choose **Save Slack channel**. To stop posting, pick **No channel** and save. Project managers and administrators can change it. Each task gets one message in the channel, and its next updates go in that thread.',
      leonardoTitle: 'Ask @leonardo',
      leonardo: '**@leonardo** is Vammo’s agent in Slack and it is connected to the tracker. Mention it in a channel or a thread and it works as you, with your permissions. For example:',
      e1: 'File a request from what you describe: “@leonardo file a request: riders cannot see their swap history.”',
      e2: 'Create a task for a team: “@leonardo create a task for Platform to add a filter to the swap history, high priority.”',
      e3: 'Read a whole conversation and turn it into work: “@leonardo read this thread and create the follow-up tasks for Fleet Operations.”',
      e4: 'Look things up: “@leonardo what is the status of VAM-42?” or “what is still open in Rider App?”',
      e5: 'Assign and mention the people in the conversation: it recognizes Slack members by their company email.',
      noteTitle: 'Good to know',
      note: 'What @leonardo does shows in the history as you (via Clawbot), and it never has administrator permissions. The `/vammo-work` command and the **Create Vammo request** shortcut keep working too.',
    },
    tracking: {
      title: 'Code work is tracked automatically',
      intro: 'You do not need to update tickets by hand. When you code with a connected assistant or open pull requests, the tracker records the work and links it to the right task.',
      f1: {
        title: 'You start a task',
        body: 'With the plugin installed, the assistant registers your objective, repository and branch. It reuses the task you name (for example `VAM-42`) or the one already linked to your branch or session, and creates a new one only when nothing matches. The task moves to **In progress** by itself. Tasks an agent creates on its own show a robot icon, and the **Created by agents** filter lists them so the team can review them.',
      },
      f2: { title: 'Milestones are recorded', body: 'Progress, the checks it actually ran, blockers and pull requests are added to the task as the session advances. A small file inside `.git/` remembers the link between sessions and is never committed.' },
      f3: { title: 'GitHub confirms it', body: 'A pull request whose title, branch or description mentions a key such as `VAM-42`, or whose branch matches an agent session, is linked automatically. Attach the repository to a project; besides webhooks, the tracker reconciles every 15 minutes.' },
      f4: {
        title: 'Delivery is verified',
        body: 'When every linked pull request is merged, production work moves to integrated. It reaches **Done** by itself once the tracker sees a production deployment that contains its code; nobody has to declare which services it touches. A merged PR or an ended session is not enough.',
      },
      keyTipTitle: 'Tip: put the key in the branch',
      keyTip: 'A branch such as `feat/VAM-42-swap-history`, or `VAM-42` in the pull request title, links the work with certainty, even without an assistant.',
      ambiguousTitle: 'When the match is uncertain',
      ambiguous: 'Similar titles are only suggestions. If several tasks could match, the assistant shows you the candidates and asks. If work must go on meanwhile, it creates a provisional item that shows up in the exceptions inbox to confirm or merge; nothing is lost.',
    },
  },
  table: {
    total_one: '{{count}} item',
    total_other: '{{count}} items',
    showing_one: '{{count}} loaded',
    showing_other: '{{count}} loaded',
    sortBy: 'Sort by {{column}}',
    ascending: 'Ascending order',
    descending: 'Descending order',
    people: 'People',
    peopleRole: 'Role',
    clearPeople: 'Clear people',
    selected: 'Selected',
    activeFilters: 'Active filters',
    removeFilter: 'Remove filter',
    noOwner: 'Nobody',
    createdBy: 'Created by',
    empty: 'Empty',
    due: {
      overdue: 'Overdue',
      soon: 'Next 7 days',
      none: 'No due date',
      set: 'With a due date',
    },
    editMode: 'Edit in table',
    editModeOn: 'Editing in the table: changes save as you make them. Open an item from its title.',
    saved: 'Saved',
    phaseMoveHint: 'Move to…',
    searchPeople: 'Search people',
  },
  fields: {
    documentsTitle: 'Document properties',
    documentsHint: 'Properties every document can have, like status, owner or type. They also filter the documents table.',
    title: 'Custom fields',
    projectTitle: 'Project fields',
    hint: 'Typed fields on work items. Archiving a field hides it; its values are kept.',
    manage: 'Manage fields',
    section: 'Fields',
    name: 'Name',
    type: 'Type',
    add: 'Add field',
    created: 'Field created',
    empty: 'No fields yet.',
    archived: 'Archived',
    archive: 'Archive field',
    restore: 'Restore field',
    moveUp: 'Move up',
    moveDown: 'Move down',
    optionName: 'Option',
    newOption: 'New option',
    addOption: 'Add',
    removeOption: 'Remove {{name}}',
    none: 'No fields apply to this item.',
    filterTitle: 'Field',
    global: 'All work',
    types: {
      TEXT: 'Text',
      NUMBER: 'Number',
      BOOLEAN: 'Yes / no',
      DATE: 'Date',
      SINGLE_SELECT: 'Single choice',
      MULTI_SELECT: 'Multiple choice',
    },
  },
  calendar: {
    title: 'Calendar',
    previous: 'Previous month',
    next: 'Next month',
    today: 'Today',
    count_one: '{{count}} item due this view',
    count_other: '{{count}} items due this view',
    hint: 'Drag an item to change its due date',
    more_one: '+{{count}} more',
    more_other: '+{{count}} more',
  },
  similar: {
    title: 'Similar existing work',
    aiTitle: 'Possible duplicates',
    aiNone: 'The AI found no duplicates among the similar records.',
    check: 'Check with AI',
    checkRequests: 'Check duplicates with AI',
    noTextMatches: 'No similar requests found by text.',
    byAi: 'AI',
    fixture: 'Test mode',
    confidence: {
      HIGH: 'Same thing',
      MEDIUM: 'Probably the same',
      LOW: 'Similar text',
    },
  },
  triageAssist: {
    title: 'AI triage suggestion',
    hint: 'Proposes priority, team, project, type and labels from the request. Nothing changes until you apply it.',
    suggest: 'Suggest',
    again: 'Ask again',
    apply: 'Apply',
    ask: 'Ask the requester',
    projectHint: 'Move it with the project field to review who will see it.',
    createWork: 'Create work with this',
  },
  automations: {
    title: 'Automations',
    subtitle: 'Rules that act on work when something happens: assign, prioritize, label, set dates or notify.',
    new: 'New rule',
    edit: 'Edit rule',
    dialogHint: 'Rules act as the system, at most once per change, and their own changes never trigger rules.',
    scope: 'Scope',
    allScopes: 'All rules I can see',
    showArchived: 'Show archived',
    empty: 'No automation rules yet',
    emptyHint: 'Project managers create rules for their projects; administrators for all work.',
    name: 'Name',
    trigger: 'Trigger',
    when: 'When',
    if: 'if',
    ifTitle: 'If',
    then: 'Then',
    any: 'Any',
    phases: 'Phase now',
    fromPhases: 'Phase before',
    labelsAny: 'Has any of these labels',
    assignment: 'Assignment',
    unassignedYes: 'nobody assigned',
    unassignedNo: 'someone assigned',
    action: 'Action',
    addAction: 'Add action',
    removeAction: 'Remove action',
    enabled: 'Enabled',
    saved: 'Rule saved',
    archive: 'Archive rule',
    restore: 'Restore rule',
    runs: 'Recent runs',
    runsTitle: 'Runs of {{name}}',
    noRuns: 'This rule has not run yet.',
    hiddenItem: 'An item you cannot see',
    runCount_one: 'Ran {{count}} time',
    runCount_other: 'Ran {{count}} times',
    lastRun: 'last',
    lastError: 'Last error: {{error}}',
    runStatus: {
      APPLIED: 'Applied',
      FAILED: 'Failed',
    },
    unassign: 'Nobody (unassign)',
    days: 'Days',
    daysAfter: 'days after the change',
    onlyIfEmpty: 'Only if it has no due date',
    chooseField: 'Choose a field',
    message: 'Message',
    messagePlaceholder: 'What should they know or do?',
    addPerson: 'Add person',
    people_one: '{{count}} person',
    people_other: '{{count}} people',
    manage: 'Automation rules',
    byRule: 'Automation: {{name}}',
    triggers: {
      WORK_CREATED: 'work is created',
      PHASE_CHANGED: 'the phase changes',
      PRIORITY_CHANGED: 'the priority changes',
      ASSIGNEE_CHANGED: 'the assignee changes',
      LABELS_CHANGED: 'the labels change',
      DUE_DATE_CHANGED: 'the due date changes',
      BLOCKED: 'work gets blocked',
    },
    actions: {
      SET_PRIORITY: 'Set priority',
      SET_ASSIGNEE: 'Assign to',
      ADD_LABELS: 'Add labels',
      REMOVE_LABELS: 'Remove labels',
      SET_DUE_IN_DAYS: 'Set due date',
      SET_FIELD: 'Set a field',
      NOTIFY: 'Notify',
    },
    recipients: {
      ASSIGNEE: 'Assignee',
      OWNER: 'Owner',
      CREATOR: 'Creator',
      FOLLOWERS: 'Followers',
    },
    text: {
      SET_PRIORITY: 'set priority to {{value}}',
      SET_ASSIGNEE: 'assign to {{value}}',
      UNASSIGN: 'remove the assignee',
      ADD_LABELS: 'add labels {{value}}',
      REMOVE_LABELS: 'remove labels {{value}}',
      SET_DUE_IN_DAYS_one: 'set the due date {{count}} day later',
      SET_DUE_IN_DAYS_other: 'set the due date {{count}} days later',
      SET_DUE_IF_EMPTY_one: 'if it has no due date, set one {{count}} day later',
      SET_DUE_IF_EMPTY_other: 'if it has no due date, set one {{count}} days later',
      SET_FIELD: 'set {{field}} to',
      NOTIFY: 'notify {{who}}: "{{message}}"',
    },
  },
  dashboards: {
    title: 'Dashboards',
    subtitle: 'Your own views of work: counts, charts and lists over any filters. Everyone sees only what they can access.',
    choose: 'Dashboard',
    new: 'New dashboard',
    name: 'Name',
    withStarter: 'Start with useful widgets',
    empty: 'No dashboards yet',
    emptyHint: 'Create one with open work, overdue items, workload per person and the weekly trend, then adjust it.',
    createStarter: 'Create my first dashboard',
    sharedTag: 'shared',
    share: 'Shared with everyone',
    edit: 'Edit',
    doneEditing: 'Done',
    more: 'More actions',
    rename: 'Rename',
    copy: 'Make a copy',
    copyName: '{{name}} (copy)',
    delete: 'Delete dashboard',
    confirmDelete: 'Delete "{{name}}"? Widgets are removed; work items do not change.',
    noWidgets: 'This dashboard has no widgets yet',
    addWidget: 'Add widget',
    editWidget: 'Edit widget',
    removeWidget: 'Remove widget',
    moveBack: 'Move earlier',
    moveForward: 'Move later',
    saved: 'Dashboard saved',
    widgetTitle: 'Title',
    kind: 'Widget',
    size: 'Size',
    groupBy: 'Group by',
    limit: 'Items',
    weeks: 'Weeks',
    filters: 'Which work',
    filtersHint: 'The same filters as the work list. "Whoever is viewing" makes the widget personal for each reader.',
    fromView: 'Copy filters from a saved view…',
    viewer: 'Whoever is viewing',
    none: 'None',
    noData: 'Nothing matches these filters.',
    items: 'Items',
    created: 'Created',
    completed: 'Completed',
    seeAll: 'See all {{count}}',
    kinds: {
      COUNT: 'Number',
      BAR: 'Bar chart',
      PIE: 'Donut chart',
      LIST: 'List',
      TREND: 'Weekly trend',
    },
    sizes: {
      S: 'Small',
      M: 'Half width',
      L: 'Full width',
    },
    groups: {
      phase: 'Phase',
      priority: 'Priority',
      type: 'Type',
      assignee: 'Assignee',
      owner: 'Owner',
      project: 'Project',
      team: 'Team',
      label: 'Label',
    },
    sorts: {
      updatedAt: 'Recently updated',
      dueDate: 'Due date',
      priority: 'Priority',
      createdAt: 'Newest',
    },
    starter: {
      name: 'My dashboard',
      open: 'Open work',
      overdue: 'Overdue',
      blocked: 'Blocked',
      byPhase: 'Work by phase',
      byAssignee: 'Open work per person',
      trend: 'Created and completed per week',
      urgent: 'Urgent and high priority',
    },
  },
  agentCreated: {
    label: 'Created by an agent',
    origin: 'Origin',
    filter: 'Created by agents',
    filterPeople: 'Created by people',
    hint: 'An AI agent created this on its own to track its work. Items you create, or ask an agent to create, have no robot.',
    provisionalHint: 'It may duplicate existing work: confirm or merge it.',
  },
  workWeight: {
    unit: 'pts',
    scored: 'Size estimated automatically by TypeSafe Jev from the item and its pull requests when it was completed (1, 2, 3, 5, 8 or 13). Reopening does not add points; the item keeps its highest score.',
    parent: 'Sum of the points of its sub-items.',
    zero: '0 points: no pull request, commit or progress was linked when it was completed.',
    partial: 'Some completions are not weighed yet; the points so far may still rise.',
    pending: 'Not weighed yet; scoring runs within about 25 minutes of completion.',
    unscored: 'Could not be weighed: too little information or the scoring failed.',
  },
  noEvidence: {
    badge: 'No evidence',
    hint: 'Closed by a person without deployment or deliverable evidence',
    filter: 'Closed without evidence',
    dialogHint: 'The item becomes Done and is marked "No evidence". Say why it is finished, for example where it was deployed or checked.',
  },
  databases: {
    title: 'Database title',
    untitled: 'Untitled',
    restricted: 'Restricted database',
    restrictedRow: 'Restricted',
    loadFailed: 'The database could not be loaded.',
    menu: 'Database actions',
    openFullPage: 'Open as a full page',
    inDocument: 'In {{name}}',
    archive: 'Archive database',
    archiveConfirm: 'Archive this database? It becomes read-only everywhere it is shown, and its editors can restore it.',
    archivedNotice: 'This database is archived and read-only.',
    archivedBadge: 'Archived',
    archivedToast: 'Database archived',
    restore: 'Restore',
    restoredToast: 'Database restored',
    removeBlock: 'Remove from this document',
    search: 'Search',
    noMatches: 'No rows match.',
    loadMore: 'Show more ({{shown}} of {{total}})',
    rowCount_one: '{{count}} row',
    rowCount_other: '{{count}} rows',
    export: {
      more: '… {{count}} more rows',
    },
    defaults: {
      title: 'Untitled database',
      name: 'Name',
      status: 'Status',
      notStarted: 'Not started',
      inProgress: 'In progress',
      done: 'Done',
      tableView: 'Table',
      boardView: 'Board',
      listView: 'List',
      calendarView: 'Calendar',
      untitledRow: 'Untitled',
      relatedRows: 'Related',
    },
    blocks: {
      section: 'Databases',
      table: 'Database — table',
      board: 'Database — board',
      linked: 'Linked database',
      linkedHint: 'Show a database you can read here too. It is the same data: changes show everywhere, and people still need access to its document.',
      searchDatabases: 'Search databases',
      noDatabases: 'No databases you can read match.',
    },
    section: {
      title: 'Databases created in this document',
      notShown: 'Not shown in the document',
      insert: 'Show here',
    },
    types: {
      TITLE: 'Title',
      TEXT: 'Text',
      NUMBER: 'Number',
      SELECT: 'Select',
      MULTI_SELECT: 'Multi-select',
      STATUS: 'Status',
      DATE: 'Date',
      PERSON: 'Person',
      CHECKBOX: 'Checkbox',
      URL: 'Link',
      RELATION: 'Relation',
    },
    numberFormat: {
      NUMBER: 'Number',
      INTEGER: 'Whole number',
      PERCENT: 'Percent',
      BRL: 'Real (R$)',
      USD: 'US dollar ($)',
      EUR: 'Euro (€)',
    },
    statusGroup: {
      TODO: 'To do',
      IN_PROGRESS: 'In progress',
      DONE: 'Complete',
    },
    property: {
      add: 'Add a property',
      create: 'Add property',
      name: 'Property name',
      type: 'Type',
      menu: '{{name}} column menu',
      resize: 'Resize {{name}}',
      numberFormat: 'Number format',
      editOptions: 'Edit options',
      editOptionsOf: 'Options of {{name}}',
      optionColor: 'Color of {{name}}',
      optionName: 'Option name',
      optionGroup: 'Status group',
      removeOption: 'Remove {{name}}',
      addOption: 'Add an option',
      newOption: 'Option {{n}}',
      removeOptionsConfirm_one: 'Remove {{count}} option? Cells that use it become empty.',
      removeOptionsConfirm_other: 'Remove {{count}} options? Cells that use them become empty.',
      hide: 'Hide in this view',
      delete: 'Delete property',
      deleteConfirm: 'Delete the property “{{name}}” and all its values?',
      confirmLoss: 'Change the type anyway?',
      relatedDatabase: 'Related database',
      thisDatabase: 'This database ({{name}})',
      twoWay: 'Also show the relation in the other database',
    },
    cell: {
      searchOptions: 'Search options',
      searchOrCreate: 'Search or create an option',
      createOption: 'Create',
      removeOption: 'Remove {{name}}',
      searchPeople: 'Search people',
      removePerson: 'Remove {{name}}',
      searchRows: 'Search rows',
      removeRelation: 'Remove {{name}}',
      today: 'Today',
      clear: 'Clear',
      empty: 'Empty',
    },
    row: {
      new: 'New',
      open: 'Open',
      openAsPage: 'Open as page',
      goToPage: 'Go to its page',
      insertBelow: 'Insert row below',
      delete: 'Delete row',
      menu: 'Actions for {{name}}',
      actions: 'Row actions',
      edited: 'Last edited by',
    },
    view: {
      tabs: 'Views',
      add: 'Add a view',
      menu: '{{name}} view actions',
      rename: 'Rename view',
      renamePrompt: 'View name',
      delete: 'Delete view',
      deleteConfirm: 'Delete the view “{{name}}”? The rows stay.',
      properties: 'Properties',
      shown: 'Shown',
      hidden: 'Hidden',
      readerChanges: 'Your filters and sorts apply only for you; the document’s editors change the saved view.',
      kind: {
        TABLE: 'Table',
        BOARD: 'Board',
        LIST: 'List',
        CALENDAR: 'Calendar',
      },
    },
    filter: {
      title: 'Filter',
      none: 'No filters: every row shows.',
      where: 'Where',
      and: 'and',
      property: 'Filter property',
      operator: 'Condition',
      value: 'Value',
      add: 'Add a filter',
      remove: 'Remove filter',
      clear: 'Clear filters',
      checked: 'Checked',
      unchecked: 'Unchecked',
      me: 'Me',
      op: {
        eq: 'is',
        neq: 'is not',
        contains: 'contains',
        not_contains: 'does not contain',
        gt: 'is greater than',
        gte: 'is at least',
        lt: 'is less than',
        lte: 'is at most',
        empty: 'is empty',
        not_empty: 'is not empty',
      },
      date: {
        eq: 'is on',
        lt: 'is before',
        gt: 'is after',
        lte: 'is on or before',
        gte: 'is on or after',
      },
    },
    sort: {
      title: 'Sort',
      none: 'No sorts: rows keep their order.',
      property: 'Sort property',
      direction: 'Direction',
      asc: 'Sort ascending',
      desc: 'Sort descending',
      add: 'Add a sort',
      remove: 'Remove sort',
    },
    board: {
      groupBy: 'Group by',
      chooseGroup: 'Choose the property that makes the columns.',
      needsGroup: 'A board groups rows by a select, status or person property. Add one to use this view.',
      none: 'No {{name}}',
      label: 'Board grouped by {{name}}',
    },
    calendar: {
      dateProperty: 'Date property',
      chooseDate: 'Choose the date property that places rows on the calendar.',
      needsDate: 'A calendar places rows by a date property. Add one to use this view.',
      previous: 'Previous month',
      next: 'Next month',
      today: 'Today',
      by: 'By {{name}}',
      label: 'Calendar of {{name}}',
      add: 'Add a row on {{date}}',
      more: '+{{count}} more',
    },
  },
  time: {
    justNow: 'just now',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };
/** Every locale must provide exactly the English structure. */
export type Messages = Widen<typeof en>;
